import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { CONTROL_SIZE, controlSizeStyles } from '@schmancy/theme/control-size'
import { SchmancyTheme } from '@schmancy/theme/theme.interface'
import { theme } from '@schmancy/theme/theme.service'
import { css, html, PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { createRef, ref, Ref } from 'lit/directives/ref.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import {
	animationFrames,
	BehaviorSubject,
	catchError,
	combineLatest,
	debounceTime,
	defer,
	distinctUntilChanged,
	EMPTY,
	exhaustMap,
	filter,
	finalize,
	first,
	firstValueFrom,
	from,
	fromEvent,
	map,
	merge,
	Observable,
	of,
	race,
	retry,
	shareReplay,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
	throwError,
	timer,
	timeout,
} from 'rxjs'

// The tags this widget composes live elsewhere in the library; import their
// concrete modules so they self-register when `schmancy-revolute` is loaded
// standalone (the consumer's auto-import never sees them otherwise).
import '@schmancy/form/fields/input/input'
import '@schmancy/primitives/alert/alert'
import '@schmancy/primitives/busy/busy'
import type { SchmancyButton } from '@schmancy/primitives/button/button'
import '@schmancy/primitives/button/button'
import '@schmancy/primitives/details/details'
import type SchmancyDetails from '@schmancy/primitives/details/details'
import '@schmancy/primitives/skeleton/skeleton'
import '@schmancy/primitives/surface/surface'
import '@schmancy/primitives/typography/typography'
import { fieldChromeClasses, fieldChromeClassNames } from '@schmancy/form/fields/input/input-chrome'
import type { SchmancyInputChangeEvent } from '@schmancy/form/fields/input/input'
import type { SchmancyAlert, SchmancyAlertNotice } from '@schmancy/primitives/alert/alert'

/**
 * The single source of truth for one express-payment slot (Apple/Google Pay,
 * Revolut Pay) — both what it shows AND whether it can be clicked derive
 * from this ONE value (see `ExpressSlot` below), never from separate
 * per-channel writes that have to be kept in sync by discipline alone.
 *
 * `status`: 'pending' reserves the slot's settled height and shows its
 * skeleton; 'available' cross-fades to the mounted SDK button at that same
 * height (zero layout shift); 'unavailable' is the one legitimate height
 * change — a collapse out of the layout. These three cover the slot's
 * boot-time resolution and any later flip between available and
 * unavailable. A re-price of an already-'available' slot never re-enters
 * 'pending' — it double-buffers a fresh instance into the slot's other
 * mount container and cross-fades directly to it (see
 * `updatePaymentMethodsObservable`), leaving `status` untouched throughout.
 *
 * `front`: which of the slot's two double-buffered mount containers (A/B)
 * is currently the live, interactive one — the other is the inert
 * double-buffer target a silent rebuild mounts its fresh instance into.
 */
type SlotState = { status: 'pending' | 'available' | 'unavailable'; front: 'A' | 'B' }

/** Matches the Revolut SDK's `buttonStyle.size: 'large'` wallet buttons — the
 * reserved height for the Apple/Google Pay and Revolut Pay slots (Tailwind `h-12`). */
const WALLET_SLOT_HEIGHT_PX = CONTROL_SIZE.md.height

/** A vendor SDK call (or the host's own order-token promise) with no
 * built-in timeout would otherwise pin loading$/inert/the exhaustMap gate
 * forever with no recovery but a reload — every such await in this file
 * races this bound instead (see `withVendorTimeout`). */
const VENDOR_AWAIT_TIMEOUT_MS = 15_000

/** A rebuild that ends kept-stale (see `initializeExpressPayments`'s doc) is
 * exactly the case rebuildTick$'s own filter refuses to retry, since live
 * amount/currency hasn't moved on from what was just attempted. This is the
 * delay before the one bounded auto-retry that compensates for it (see
 * connectedCallback's staleDivergence$ leg). */
const STALE_RETRY_DELAY_MS = 4_000

// Event detail types
export interface SchmancyRevoluteSuccessDetail {
	/** Order token (called publicId in SDK, token in API - same value) */
	token?: string
	timestamp: number
}

export interface SchmancyRevoluteErrorDetail {
	error: Error
	message: string
	/** Which class the failure was sorted into — the same key the buyer's own
	 *  words were looked up from, so a host can log what it actually showed.
	 *  Absent means the silent class (`SchmancyRevoluteFailure`'s third class,
	 *  which has no member on purpose): nothing was shown to the buyer, and this
	 *  event is the failure's only surface. */
	failure?: SchmancyRevoluteFailure
}

export interface SchmancyRevoluteRequestOrderDetail {
	/** Resolve with order token from backend (maps to SDK's publicId at boundary) */
	resolve: (result: { token: string }) => void
	reject: (error: Error) => void
	/** Email address for the order (from card payment form, or the host-provided `email` prop) */
	email: string
}

// Event type declarations
export class SchmancyRevoluteSuccessEvent extends CustomEvent<SchmancyRevoluteSuccessDetail> {
	constructor(detail: SchmancyRevoluteSuccessDetail) {
		super('schmancy-revolute-success', { detail, bubbles: true, composed: true })
	}
}

export class SchmancyRevoluteErrorEvent extends CustomEvent<SchmancyRevoluteErrorDetail> {
	constructor(detail: SchmancyRevoluteErrorDetail) {
		super('schmancy-revolute-error', { detail, bubbles: true, composed: true })
	}
}

export class SchmancyRevoluteRequestOrderEvent extends CustomEvent<SchmancyRevoluteRequestOrderDetail> {
	constructor(detail: SchmancyRevoluteRequestOrderDetail) {
		super('schmancy-revolute-request-order', { detail, bubbles: true, composed: true })
	}
}

export class SchmancyRevoluteProcessingEvent extends CustomEvent<{ processing: boolean }> {
	constructor(processing: boolean) {
		super('schmancy-revolute-processing', { detail: { processing }, bubbles: true, composed: true })
	}
}

export class SchmancyRevoluteEmailChangeEvent extends CustomEvent<{ email: string }> {
	constructor(email: string) {
		super('schmancy-revolute-email-change', { detail: { email }, bubbles: true, composed: true })
	}
}

export type PaymentMethod = 'apple-pay' | 'google-pay' | 'revolut-pay' | 'card'

/**
 * What the buyer can actually DO next — the axis a single error string
 * collapses, and the only thing that decides what they are shown.
 *
 * Every member but `blocked` and `orderStale` leaves BOTH an intact form behind
 * the pay action and a live order to submit it against, so the action stays
 * live and relabels to its retry tense; they differ only in which move is worth
 * naming first:
 *
 * - `insufficientFunds` — a different card, or top this one up.
 * - `expiredCard` — a different card; nothing in the form can fix this one.
 * - `cardDetails` — the number or the security code is wrong, and this form is
 *   where the buyer can fix it; no other field it collects can be at fault.
 *   Presumes the card form is on screen, so only a card payment reaches it —
 *   a wallet sheet's own equivalent decline reads as `refused` instead.
 * - `contactDetails` — the email or the name that went with this order was
 *   rejected, and correcting it is a move the buyer has right here. Unlike
 *   `cardDetails` this is NOT method-scoped: the email rides EVERY leg's order
 *   request (see `requestOrderFromParent`), so the advice is equally true from
 *   a wallet sheet as from the card form.
 * - `authentication` — the bank's own check did not conclude; take it again.
 * - `bankDeclined` — the issuer said no for a reason only the issuer will give,
 *   and every mismatch the buyer cannot resolve on this surface lands here too.
 * - `cardLocked` — the card's PIN is locked, and only the issuing bank can
 *   unlock it; on this checkout the move is a different card.
 * - `limitReached` — a spending or frequency limit on this card, not a refusal
 *   of the card itself: later, or a different card.
 * - `cardNotActivated` — the card was never activated. Its own bank can do
 *   that; a different card finishes the order now.
 * - `cardNotSupported` — this card can never work here; another one can.
 * - `technical` — nothing about the card is wrong; the same attempt may work.
 * - `refused` — the honest fallback: the vendor named no reason we recognise.
 * - `orderStale` — the checkout this widget holds is no longer live (already
 *   completed, cancelled, or gone). Retrying against a dead order token cannot
 *   succeed, so the pay action goes disabled exactly as it does for `blocked` —
 *   but with NO remedy, because the widget's own remedy re-enters its own
 *   initialization and would ask the host for a token against the same dead
 *   session. The move belongs to the host: it learns of it through
 *   `schmancy-revolute-error`'s `failure`, and starting a fresh checkout is its
 *   answer. The buyer's copy says exactly that and promises nothing this
 *   surface can't keep.
 * - `blocked` — there is nothing to submit. The pay action would be a lie, so
 *   it goes disabled and the alert ships the remedy instead.
 *
 * A third class exists and has no member here on purpose: a failure the buyer
 * has no move for and no stake in (a stale wallet refresh the widget already
 * auto-retries, a colour restyle that could not remount) is a console warning
 * and never a notice.
 */
export type SchmancyRevoluteFailure =
	| 'insufficientFunds'
	| 'expiredCard'
	| 'cardDetails'
	| 'contactDetails'
	| 'authentication'
	| 'bankDeclined'
	| 'cardLocked'
	| 'limitReached'
	| 'cardNotActivated'
	| 'cardNotSupported'
	| 'technical'
	| 'refused'
	| 'orderStale'
	| 'blocked'

/**
 * One notice's buyer-facing words. Every message carries the fact that no
 * money was taken; none of them ever carries a vendor string.
 */
export interface SchmancyRevoluteNoticeText {
	/** Names what happened, in money words — the first thing read. */
	headline: string
	/** Why, and what to do next. Carries the "nothing was charged" fact. */
	body: string
	/** `blocked` only: the in-context affordance that satisfies the unmet
	 *  precondition. Its press dispatches nothing to the host — the widget
	 *  retries its own initialization, which is why `orderStale` (whose
	 *  precondition only the host can satisfy) carries none. */
	remedy?: string
}

/** Host copy override, keyed by what the buyer can do — the same shape and
 *  the same verbatim contract `payLabel` already has for the CTA. Derived from
 *  the failure union rather than listed in parallel with it, so a member added
 *  there is a compile error here instead of a notice with no words. The widget's
 *  own words are the same guarantee one level down: it builds its wording key
 *  from the failure member (`payment.notice.<failure>.<slot>`), so a new member
 *  with no entries in the table is a compile error at that read.
 *
 *  `blocked` requires its `remedy` and no other member has one, because the
 *  remedy re-enters THIS widget's own initialization and `blocked` is the only
 *  failure that recovers from exactly that. Every decline member leaves the pay
 *  action live as its own retry and needs no second affordance; `orderStale`
 *  disables the action alongside `blocked` and still takes no remedy, since
 *  re-initializing against a dead checkout would offer the buyer a button that
 *  cannot work — its recovery is the host's, signalled by
 *  `schmancy-revolute-error`. A host localizing these in parallel would
 *  otherwise drop the `remedy` the other members legitimately omit and ship a
 *  permanently dead checkout, so the type refuses that pair rather than
 *  trusting it not to happen. */
export type SchmancyRevoluteNoticeCopy = Record<
	Exclude<SchmancyRevoluteFailure, 'blocked'>,
	SchmancyRevoluteNoticeText
> & {
	blocked: SchmancyRevoluteNoticeText & { remedy: string }
}

/**
 * A failure the buyer is told about, and the vendor's own account of it.
 *
 * `detail` is TELEMETRY ONLY and never reaches a screen — it rides
 * `SchmancyRevoluteErrorEvent` for the host to log, while what the buyer reads
 * is looked up from `failure` in `noticeCopy`.
 */
interface PaymentNotice {
	failure: SchmancyRevoluteFailure
	detail: string
}

/** Every vendor SDK success/error/click callback (Apple/Google Pay, Revolut Pay,
 * card) funnels through this one union so the error/tracking/dispatch fan-out
 * lives in a single subscription rather than duplicated per payment method. */
type SdkPaymentEvent =
	| { kind: 'success'; method: PaymentMethod; token?: string }
	// `failure` is classified at the ingestion point, where the SDK's structured
	// `type`/`code` are still in hand — `message` is the only field that survives
	// into the fan-out, and it is the one field that must never decide anything.
	| { kind: 'error'; method: PaymentMethod; failure: SchmancyRevoluteFailure; message: string }
	| { kind: 'click'; method: PaymentMethod }

/**
 * Tracking steps for payment analytics
 * Covers the full user journey through payment
 */
export type SchmancyRevoluteTrackingStep =
	// Visibility events
	| 'PAYMENT_VIEW' // Component became visible/initialized
	| 'PAYMENT_METHOD_AVAILABLE' // A payment method became available (Apple Pay, Google Pay, Revolut Pay)
	// Interaction events
	| 'PAYMENT_METHOD_CLICKED' // User clicked on a payment method button
	| 'CARD_FIELD_FOCUSED' // User focused on card input field
	| 'CARD_FIELD_BLUR' // User left card input field
	| 'EMAIL_ENTERED' // User entered email address
	// Submission events
	| 'CARD_SUBMIT_STARTED' // Card payment submission initiated
	| 'CARD_VALIDATION_FAILED' // Card validation failed
	// Outcome events
	| 'PAYMENT_SUCCESS' // Payment completed successfully
	| 'PAYMENT_ERROR' // Payment failed with error
	| 'PAYMENT_CANCELLED' // User dismissed an in-flight payment (e.g. closed a 3DS challenge)

export interface SchmancyRevoluteTrackingDetail {
	step: SchmancyRevoluteTrackingStep
	/** Payment method involved (if applicable) */
	method?: PaymentMethod
	/** Additional data for the event */
	data?: Record<string, unknown>
}

export class SchmancyRevoluteTrackingEvent extends CustomEvent<SchmancyRevoluteTrackingDetail> {
	constructor(step: SchmancyRevoluteTrackingStep, method?: PaymentMethod, data: Record<string, unknown> = {}) {
		super('schmancy-revolute-tracking', { detail: { step, method, data }, bubbles: true, composed: true })
	}
}

// ============================================================================
// Revolut SDK Type Definitions (from official documentation)
// https://developer.revolut.com/docs/sdks/merchant-web-sdk/introduction
// ============================================================================

/** Revolut checkout error structure. `type` is a stable key the SDK derives
 * from the API's `decline_reason` through a fixed map, and is optional here
 * because this widget cannot guarantee a newer API build's reason is in that
 * map. `message` is `customer_bank_message` when the issuer supplies one — the
 * raw vendor string, which is why nothing classifies on it (see
 * `classifyRevoluteFailure`). */
interface RevolutCheckoutError {
	type?: string
	message: string
	code?: number
}

/** Redirect URLs configuration for Revolut Pay */
interface RevolutRedirectUrls {
	success: string
	failure: string
	cancel: string
}

/** Button style options for Revolut Pay and Payment Request */
interface RevolutButtonStyle {
	height?: string
	radius?: 'none' | 'small' | 'large' | 'round'
	size?: 'none' | 'small' | 'large'
	variant?: 'light' | 'dark' | 'light-outlined'
	action?: 'subscribe' | 'donate' | 'pay' | 'buy'
	cashback?: boolean
}

/** The SDK adds a minimum-width gutter to its iframe. The native section
 * owns the shared radius and clipping, so both visible corners stay equal
 * without overriding anything inside the vendor frame. */
function walletButtonStyle(): RevolutButtonStyle {
	return { height: `${WALLET_SLOT_HEIGHT_PX}px`, size: 'large', radius: 'none', variant: 'dark' }
}

/** Revolut Pay mount options (official SDK type) */
interface RevolutPayMountOptions {
	currency: string
	totalAmount: number
	createOrder: () => Promise<{ publicId: string }>
	buttonStyle?: RevolutButtonStyle
	redirectUrls?: RevolutRedirectUrls
	mobileRedirectUrls?: RevolutRedirectUrls
	requestShipping?: boolean
	savePaymentMethodForMerchant?: boolean
	validate?: () => Promise<boolean> | boolean
}

/** Payment Request options for Apple Pay / Google Pay (official SDK type) */
interface RevolutPaymentRequestOptions {
	amount: number
	currency: string
	createOrder: () => Promise<{ publicId: string }>
	buttonStyle?: RevolutButtonStyle
	onSuccess?: () => void
	onError?: (error: RevolutCheckoutError) => void
	preferredPaymentMethod?: RevolutPaymentMethod | RevolutPaymentMethod[]
	requestShipping?: boolean
	requestPayerInfo?: { phone?: boolean }
}

/** Available payment methods for Payment Request API */
type RevolutPaymentMethod = 'applePay' | 'googlePay'

/** Card field style options */
interface RevolutCardFieldStyles {
	default?: Record<string, string | Record<string, string>>
	focused?: Record<string, string | Record<string, string>>
	invalid?: Record<string, string | Record<string, string>>
	empty?: Record<string, string | Record<string, string>>
	autofilled?: Record<string, string | Record<string, string>>
	completed?: Record<string, string | Record<string, string>>
}

/** Card field options (official SDK type) */
interface RevolutCardFieldOptions {
	target: HTMLElement
	onSuccess?: () => void
	onError?: (error: RevolutCheckoutError) => void
	onValidation?: (messages: string[]) => void
	onCancel?: () => void
	locale?: string
	showLoadingIndicator?: boolean
	styles?: RevolutCardFieldStyles
}

/** Card field submit meta (official SDK type) */
interface RevolutCardFieldMeta {
	name?: string
	email?: string
	phone?: string
	savePaymentMethodFor?: string
	cardholderName?: string
	billingAddress?: RevolutAddress
	shippingAddress?: RevolutAddress
}

/** Address structure for Revolut */
interface RevolutAddress {
	countryCode: string
	region?: string
	city?: string
	postcode: string
	streetLine1?: string
	streetLine2?: string
}

/** Card field instance methods */
interface RevolutCardFieldInstance {
	submit: (meta: RevolutCardFieldMeta) => void
	validate: () => void
	destroy: () => void
}

/** Payment request instance methods */
interface RevolutPaymentRequestInstance {
	destroy: () => void
	render: () => Promise<void>
	canMakePayment: () => Promise<RevolutPaymentMethod | null>
}

/** Revolut Pay instance methods */
interface RevolutPayInstance {
	mount: (target: HTMLElement, options: RevolutPayMountOptions) => Promise<void>
	destroy: () => void
	on: (event: 'payment', callback: (event: RevolutPayEvent) => void) => void
}

/** Revolut Pay payment event
 * Note: SDK returns orderId in newer versions, publicId in older versions
 * Both refer to the same value (order token)
 */
interface RevolutPayEvent {
	type: 'success' | 'error' | 'cancel' | 'click'
	orderId?: string
	publicId?: string // Legacy field, same as orderId
	error?: RevolutCheckoutError
	dropOffState?: string // For cancel events
}

/** RevolutCheckout.payments() return type */
interface RevolutPaymentsResult {
	revolutPay: RevolutPayInstance
	paymentRequest: (target: HTMLElement, options: RevolutPaymentRequestOptions) => RevolutPaymentRequestInstance
}

/** Global RevolutCheckout function type */
interface RevolutCheckoutFunction {
	(
		token: string,
		mode: 'sandbox' | 'prod',
	): {
		createCardField: (options: RevolutCardFieldOptions) => RevolutCardFieldInstance
	}
	payments: (options: { publicToken: string; locale?: string }) => Promise<RevolutPaymentsResult>
}

// Extend Window interface for Revolut SDK
declare global {
	interface Window {
		RevolutCheckout: RevolutCheckoutFunction
	}
}

/** The SDK collapses the API's `decline_reason` into one of these keys before
 * any callback sees it, so this is the stable axis — the same reason arrives
 * under the same key whatever the issuer wrote in `message`. A key absent here
 * (`error.unknown` included, deliberately) lands on `refused`.
 *
 * A Map, not an object: the vendor chooses these key strings, and a `type` of
 * `constructor` or `toString` read off an object would resolve through
 * `Object.prototype` to something truthy that is not a member at all, so the
 * `refused` fallback would never fire and the notice would raise with a tone
 * and no words. */
const FAILURE_BY_ERROR_TYPE = new Map<string, SchmancyRevoluteFailure>([
	['error.insufficient-funds', 'insufficientFunds'],
	['error.expired-card', 'expiredCard'],
	// The card form collects a number, an expiry and a security code, and the
	// buyer can retype any of them — so these are the only two keys whose advice
	// points at a CARD field they can actually change here.
	['error.incorrect-cvv-code', 'cardDetails'],
	['error.invalid-card', 'cardDetails'],
	// Correctable here too, but off a different form: the email goes out with
	// EVERY leg's order request and the name with the card submit, both from
	// values this widget either renders a field for or takes from the host prop
	// its own contact block filled. That difference in origin is why these carry
	// no method guard while the card keys above do (see classifyRevoluteFailure).
	['error.invalid-email', 'contactDetails'],
	['error.email-is-not-specified', 'contactDetails'],
	['error.invalid-cardholder-name', 'contactDetails'],
	['error.invalid-name', 'contactDetails'],
	['error.3ds-failed', 'authentication'],
	['error.unauthenticated-access', 'authentication'],
	['error.rejected-by-customer', 'authentication'],
	['error.do-not-honour', 'bankDeclined'],
	['error.transaction-declined', 'bankDeclined'],
	['error.transaction_declined.description', 'bankDeclined'],
	['error.transaction_declined.high_risk_of_fraud', 'bankDeclined'],
	// stolen_card, lost_card, suspected_fraud, security_violation,
	// restricted_card, pick_up_card and law_violation all arrive under this one
	// key. The buyer is never told their card is reported stolen — they are told
	// their bank declined it, which is also the only move they actually have.
	['error.risk-too-high', 'bankDeclined'],
	['error.invalid-country', 'bankDeclined'],
	['error.invalid-pin', 'bankDeclined'],
	['error.invalid-transaction', 'bankDeclined'],
	['error.payment.account_currently_not_available', 'bankDeclined'],
	// The key whose whole content is the issuer's own sentence, which never
	// reaches a screen — so what is left to tell the buyer is that their bank
	// said no and only the bank can say why. That is `bankDeclined` exactly.
	['error.bank-message', 'bankDeclined'],
	// Neither is correctable here: the cardholder name that could mismatch is a
	// claim the ISSUER holds (not the string this widget sent — that one is
	// `contactDetails` above), and no postcode is collected at all.
	['error.customer-name-mismatch', 'bankDeclined'],
	['error.invalid-postcode', 'bankDeclined'],
	// Card-state facts, each with a move of its own that `bankDeclined`'s "ask
	// your bank why" would have buried: a locked PIN and an unactivated card are
	// already-known conditions the bank would only restate, and a limit is not a
	// refusal of the card at all — waiting is a real option only this class names.
	['error.pin-tries-exceeded', 'cardLocked'],
	['error.pin-required', 'cardLocked'],
	['error.withdrawal-limit-exceeded', 'limitReached'],
	['error.withdrawal-frequency-exceeded', 'limitReached'],
	['error.new-card-not-unblocked', 'cardNotActivated'],
	['error.unsupported-card', 'cardNotSupported'],
	['error.unsupported-payment-method', 'cardNotSupported'],
	['error.card-not-supported-in-sandbox', 'cardNotSupported'],
	// This instrument can never settle THIS payment — a region or territory the
	// card is not accepted in, a method the merchant cannot take, or a saved
	// method the SDK cannot resolve to a usable one. Another card can.
	['error.payment-method-not-supported', 'cardNotSupported'],
	['error.user_card_region_mismatch', 'cardNotSupported'],
	['error.revolut_pay_territory_error', 'cardNotSupported'],
	['error.payment-method-not-permitted-for-merchant-initiated-transactions', 'cardNotSupported'],
	['error.payment-method-not-found', 'cardNotSupported'],
	['error.payment-method-already-exists', 'cardNotSupported'],
	['error.duplicated-payment-method', 'cardNotSupported'],
	['error.technical-error', 'technical'],
	['error.invalid-merchant', 'technical'],
	['error.invalid-amount', 'technical'],
	['error.transaction-too-small', 'technical'],
	// Vendor-side faults, every one of them: nothing about the card is wrong and
	// the same attempt may work, which is exactly what `technical` promises.
	['error.internal-error', 'technical'],
	['error.internal-retry-error', 'technical'],
	['error.temporary-internal-error', 'technical'],
	['error.failed', 'technical'],
	['error.unknown-transaction-error', 'technical'],
	['error.fetch-address', 'technical'],
	// The order behind this widget is gone, so the retry the other classes offer
	// would post against a dead token. See `orderStale` on the failure union for
	// why this disables the action without a remedy.
	['error.order-already-completed', 'orderStale'],
	['error.order-is-cancelled', 'orderStale'],
	['error.order-not-found', 'orderStale'],
	['error.payment-not-found', 'orderStale'],
	['error.transaction-invalid-state', 'orderStale'],
	// No dot after "error" — verbatim what the vendor ships, typo and all.
	['error-order-already-being-processed', 'orderStale'],
	// Deliberately absent, and left to fall through to `refused`:
	// `error.unknown` (the vendor's own "no reason"), `error.invalid-address`,
	// `error.revolut-pay.invalid-shipping-address`, `error.email-in-use` and
	// `error.order-customer-id-is-not-set` — this widget collects no address and
	// mints no customer, so none of them names a move the buyer has here.
])

/** The numeric half of the same taxonomy, for a call that carries a code
 * alongside a key we do not know, or with no key at all. */
const FAILURE_BY_ERROR_CODE = new Map<number, SchmancyRevoluteFailure>([
	[1000, 'authentication'],
	[1009, 'bankDeclined'],
	[1023, 'bankDeclined'],
	[1025, 'bankDeclined'],
	[1026, 'insufficientFunds'],
	[1027, 'authentication'],
	[1028, 'expiredCard'],
	[1029, 'cardDetails'],
	[1031, 'technical'],
	[1032, 'bankDeclined'],
	[1033, 'cardNotSupported'],
	[1100, 'cardNotSupported'],
])

/**
 * The vendor's structured account of a decline → what the buyer can do about
 * it. `message` is deliberately not an input: it can be the issuer's own
 * untranslated sentence, so it is telemetry and never an axis. `type` first,
 * `code` for the calls that carry one without a key we know, and `refused`
 * for everything left — the honest "we were told no, and not why".
 *
 * `method` is required because the taxonomy is not method-independent: the same
 * key means different things to a buyer typing a card here and a buyer picking
 * one inside a wallet sheet, and a new ingestion point that omitted it would
 * ship that confusion silently.
 */
export function classifyRevoluteFailure(
	type: string | undefined,
	code: number | undefined,
	method: PaymentMethod,
): SchmancyRevoluteFailure {
	const failure = FAILURE_BY_ERROR_TYPE.get(type) ?? FAILURE_BY_ERROR_CODE.get(code) ?? 'refused'
	// `cardDetails` sends the buyer back to fields this widget renders, which
	// exist on the card leg alone. From a wallet sheet the same key names a card
	// they never typed here, so it collapses to `refused` — whose "try again, or
	// use a different card" is exactly what the sheet lets them do.
	//
	// `contactDetails` takes NO such guard, and the difference is where the
	// rejected value came from rather than which class it is: the email rides
	// requestOrderFromParent, which every leg's createOrder calls, sourced from
	// either this widget's own input or the host's `email` prop — so a wallet
	// buyer's order carries the same address a card buyer's does, and "check the
	// email on this order" is a move they have on every leg. Guarding it would
	// answer a correctable failure with `refused`'s "try a different card",
	// which cannot fix an address.
	return failure === 'cardDetails' && method !== 'card' ? 'refused' : failure
}

// Shared across every `schmancy-revolute` instance on the page — without this,
// two instances mounting concurrently before the script loads would each inject
// their own <script> tag (a duplicate SDK load, not merely a duplicate byte).
// Wrapped in `defer(...)` at every call site so a `retry` re-invokes this
// factory fresh on each attempt rather than replaying a cached failure.
let sharedRevolutSdkLoad$: Observable<void> | null = null

function loadRevolutSdkOnce(sandboxMode: boolean): Observable<void> {
	if (window.RevolutCheckout) {
		return of(undefined)
	}
	if (!sharedRevolutSdkLoad$) {
		sharedRevolutSdkLoad$ = new Observable<void>(observer => {
			const script = document.createElement('script')
			script.src = sandboxMode
				? 'https://sandbox-merchant.revolut.com/embed.js'
				: 'https://merchant.revolut.com/embed.js'
			script.async = true
			script.onload = () => {
				observer.next()
				observer.complete()
			}
			script.onerror = () => {
				sharedRevolutSdkLoad$ = null
				observer.error(new Error('Failed to load Revolut SDK'))
			}
			document.head.appendChild(script)
		}).pipe(shareReplay({ bufferSize: 1, refCount: false }))
	}
	return sharedRevolutSdkLoad$
}

/** Every occurrence of the Revolut card-field color styles resolves a schmancy
 * theme token to its computed value — the SDK's iframe can't read CSS custom
 * properties, only literal colors. */
function resolveThemeColor(token: string): string {
	return getComputedStyle(window.document.body).getPropertyValue(token.slice(4, -1))
}

/**
 * One express-payment slot's double-buffered mount points and its single
 * `SlotState` — wallet (Apple/Google Pay) and Revolut Pay are two instances
 * of this SAME machine, never copy-pasted parallel state (see
 * `SchmancyRevolute.walletSlot`/`revolutPaySlot`).
 *
 * `slot$` is the ONE value both the slot's visuals AND its interactivity
 * derive from — the click-eater bug this machine exists to kill came from
 * that truth living in five places (a status subject, a mutable
 * front-tracking field, and per-container opacity/pointer-events writes
 * from two separate call sites) that had to agree by discipline alone, and
 * any writer that updated some but not others shipped a desync. `render$()`
 * is the slot's ONLY DOM writer: every channel — section collapse,
 * skeleton, and which of the two mount containers is clickable — derives
 * together from one `SlotState` each time it changes. A transition is a
 * state write (`resolve`/`swap`/`reset`), never a direct style poke.
 */
class ExpressSlot {
	readonly slot$ = new BehaviorSubject<SlotState>({ status: 'pending', front: 'A' })
	readonly section: Ref<HTMLDivElement> = createRef() // Collapsible wrapper, reserved height
	readonly skeleton: Ref<HTMLElement> = createRef() // Cross-fades against the front container, first resolution only
	// Double-buffered mount targets: an express SDK instance caches its
	// order's amount at construction, so a re-price mounts the fresh
	// instance into whichever of these is currently the BACK container —
	// the live button stays painted in front, untouched, until the fresh
	// one confirms available and swap() cross-fades the two.
	readonly containerA: Ref<HTMLDivElement> = createRef()
	readonly containerB: Ref<HTMLDivElement> = createRef()

	/** The currently-visible, interactive mount target. */
	get frontEl(): HTMLDivElement | undefined {
		return (this.slot$.value.front === 'A' ? this.containerA : this.containerB).value
	}
	/** The currently-hidden, inert double-buffer mount target — where a
	 * silent rebuild mounts its fresh instance while the old one stays painted. */
	get backEl(): HTMLDivElement | undefined {
		return (this.slot$.value.front === 'A' ? this.containerB : this.containerA).value
	}

	/** The slot's one DOM-writing subscription leg — folded into
	 * connectedCallback's merge(), never subscribed on its own. */
	render$(): Observable<SlotState> {
		return this.slot$.pipe(
			tap(state => {
				if (this.section.value) {
					const collapsed = state.status === 'unavailable'
					this.section.value.style.maxHeight = collapsed ? '0' : `${WALLET_SLOT_HEIGHT_PX}px`
					this.section.value.style.opacity = collapsed ? '0' : '1'
					this.section.value.hidden = collapsed
					this.section.value.style.pointerEvents = collapsed ? 'none' : 'auto'
				}
				if (this.skeleton.value) {
					this.skeleton.value.style.opacity = state.status === 'pending' ? '1' : '0'
				}
				// Front/back are unconditional on status — the invariant that kills
				// the click-eater bug class. The two mount containers are fixed
				// DOM siblings (B always paints over A, absent a status branch to
				// say otherwise), so whichever is logically "front" must ALWAYS be
				// the one auto+opaque and "back" ALWAYS none+hidden — no per-status
				// exception can ever leave the two disagreeing about which is safe
				// to click.
				if (this.frontEl) {
					this.frontEl.style.opacity = '1'
					this.frontEl.style.pointerEvents = 'auto'
				}
				if (this.backEl) {
					this.backEl.style.opacity = '0'
					this.backEl.style.pointerEvents = 'none'
				}
			}),
		)
	}

	/** Boot/rebuild resolves the slot to a definitive status; front unchanged. */
	resolve(status: 'available' | 'unavailable'): void {
		this.slot$.next({ status, front: this.slot$.value.front })
	}

	/** Double-buffer cross-fade: the fresh instance just confirmed itself in
	 * the back container — flip front/back and let render$() do the fade. */
	swap(): void {
		this.slot$.next({ ...this.slot$.value, front: this.slot$.value.front === 'A' ? 'B' : 'A' })
	}

	/** New-order and disconnect reset — back to the default front (A), pending.
	 * Both containers are ALSO explicitly forced to their inert baseline here
	 * (overriding whatever render$() just painted from the state write above,
	 * when a live subscriber is listening) — a stale opacity/pointer-events
	 * left over from earlier this session must not bleed into the next
	 * checkout; the slot's next genuine resolve()/swap() repaints both
	 * containers correctly from this fresh state regardless. */
	reset(): void {
		this.slot$.next({ status: 'pending', front: 'A' })
		for (const containerRef of [this.containerA, this.containerB]) {
			if (containerRef.value) {
				containerRef.value.style.opacity = '0'
				containerRef.value.style.pointerEvents = 'none'
			}
		}
	}
}

/**
 * Revolut payment integration. Supports Apple Pay, Google Pay, Revolut Pay,
 * and Card Field.
 * @slot card-summary - Supplemental card chooser content after the visible cardDisclosureLabel.
 * @slot card-billing - Host card billing fields before the SDK card input.
 *
 * Official Documentation:
 * - Overview: https://developer.revolut.com/docs/guides/merchant/get-started
 * - Card Payments: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/card-payments/web/card-field
 * - Apple Pay / Google Pay: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/apple-pay-google-pay/web
 * - Revolut Pay: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/revolut-pay/web
 * - API Reference: https://developer.revolut.com/docs/api/merchant
 *
 * Architecture:
 * - RxJS for all reactive patterns; imperative ref-driven DOM updates for the
 *   busy overlay, collapse/fade transitions, double-buffered express-payment
 *   mounts, and the `inert`+aria-live silent-rebuild guard (never Lit's
 *   declarative re-render — those are subscription-driven, not template-driven).
 * - Tailwind CSS for styling; component-local CSS lives in `static styles`.
 *
 * Two Integration Modes:
 * 1. With Merchant Public Key → Express payments (Apple/Google/Revolut Pay) + Card Field
 * 2. Without Merchant Public Key → Card Field only
 *
 * The component never talks to a backend itself: it dispatches
 * `schmancy-revolute-request-order` and the host resolves it with an order
 * token (see `SchmancyRevoluteRequestOrderDetail`).
 *
 * Events:
 * - schmancy-revolute-request-order: fired when the component needs an order token.
 *   Detail: { resolve, reject, email } — the host creates the order and resolves/rejects.
 * - schmancy-revolute-processing: fired when payment processing state changes.
 *   Detail: { processing: boolean }
 * - schmancy-revolute-success: fired when payment succeeds.
 *   Detail: { token?: string, timestamp: number }
 * - schmancy-revolute-error: fired when payment fails.
 *   Detail: { error: Error, message: string }
 */
@schmancyElement('schmancy-revolute')
export default class SchmancyRevolute extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	// Required props
	@property({ type: Number }) amount = 0
	@property({ type: String }) currency = 'EUR'
	@property({ type: String }) merchantPublicKey = ''
	@property({ type: Boolean }) sandboxMode = false
	@property({ type: Object }) redirectUrls?: RevolutRedirectUrls // Required for Revolut Pay mobile flows

	/**
	 * Host-provided contact email. When set, the internal email input does not
	 * render — submit meta and the order request use this value verbatim. When
	 * absent, the widget collects (and validates) its own email input.
	 */
	@property({ type: String }) email?: string
	/** Host-provided payer name for the card-field submit meta. Optional, and
	 *  absent when the host has none: the meta then carries no name at all
	 *  rather than a placeholder the cardholder never gave. */
	@property({ type: String }) payerName?: string
	/** Optional card chooser label; configure before initializing payment methods. */
	@property({ type: String }) cardDisclosureLabel?: string
	/** Customer-entered billing address forwarded unchanged to the card SDK. */
	@property({ attribute: false }) billingAddress?: RevolutCardFieldMeta['billingAddress']
	@state() private cardExpanded = false
	private cardDisclosure: Ref<SchmancyDetails> = createRef()
	private walletAlert: Ref<SchmancyAlert> = createRef()
	private noticeMethod: PaymentMethod = 'card'
	private revolutPayAlert: Ref<SchmancyAlert> = createRef()
	private cardInitializing = false
	private cardOpenRequested$ = new Subject<void>()
	/** Verbatim label for the card pay button (e.g. a total-in-the-CTA string).
	 *  Falls back to `payment.pay` in the wording table. */
	@property({ type: String }) payLabel?: string
	/**
	 * Verbatim label the card pay button takes on while any decline notice is
	 * up — the same contract as `payLabel`, so a host whose pay label carries
	 * the total carries it here too and the amount survives the relabel.
	 * Falls back to `payment.retry` in the wording table.
	 */
	@property({ type: String }) retryPayLabel?: string
	/**
	 * Host copy for the inline failure notice, keyed by what the buyer can do —
	 * the per-instance seam, and it wins wholesale over the wording table the way
	 * `payLabel` wins over `payment.pay`. Left unset, each notice reads
	 * `payment.notice.<failure>.<slot>` from the table, so an app that localizes
	 * through `setComponentText` gets this panel with no prop at all. Resolved
	 * when the notice fires rather than at render: the language current at the
	 * failure is the language the panel keeps. The vendor's own words are never
	 * rendered — they ride `schmancy-revolute-error` for the host to log.
	 */
	@property({ type: Object }) noticeCopy?: SchmancyRevoluteNoticeCopy
	/**
	 * Forces the card pay button disabled regardless of internal state — the
	 * host gates on its own contact-validation and renders its own remedy line;
	 * this widget shows no reason text for that case. Express wallet buttons
	 * (Apple/Google/Revolut Pay) are unaffected — the SDK collects payer info itself.
	 */
	@property({ type: Boolean }) submitDisabled = false
	/** The host is settling a submitted card payment after SDK completion.
	 * Set only for the card action; wallet controls own their SDK feedback. */
	@property({ type: Boolean }) settling = false

	// Payment method toggles
	@property({ type: Boolean }) enableCardField = true
	@property({ type: Boolean }) enableApplePay = true
	@property({ type: Boolean }) enableGooglePay = true
	@property({ type: Boolean }) enableRevolutPay = true

	// Email input state (used only when `email` is not provided)
	@state() private emailInput = ''
	@state() private emailError = ''

	// Refs for payment containers
	private paymentContainer: Ref<HTMLDivElement> = createRef()
	// The two express-payment slots — each owns its own reserved section,
	// skeleton, and double-buffered mount containers behind ONE SlotState
	// (see ExpressSlot's doc). Same machine, two instances — no copy-paste
	// divergence between wallet and Revolut Pay.
	private readonly walletSlot = new ExpressSlot()
	private readonly revolutPaySlot = new ExpressSlot()
	private cardFieldContainer: Ref<HTMLDivElement> = createRef()
	private cardFieldSkeleton: Ref<HTMLElement> = createRef() // Cross-fades against cardFieldContainer
	private cardFieldWrapper: Ref<HTMLDivElement> = createRef() // Carries the shared <schmancy-input> chrome + error state
	// The card field's own message region — <schmancy-input>'s validation text,
	// for the wrapper that stands in for its <input>. The shared chrome is a
	// class map and nothing else, so adopting it alone leaves a red border that
	// says SOMETHING is wrong and never which thing.
	private cardFieldHint: Ref<HTMLDivElement> = createRef()
	// Visually-hidden aria-live region — the widget's own narration of a
	// silent rebuild, since `inert` removes the wallet buttons from the
	// accessibility tree and tab order with no other explanation, and this
	// rebuild outlives the host's own settling-window narration (debounce +
	// mount time). See silentRebuildActive$.
	private srAnnouncement: Ref<HTMLDivElement> = createRef()

	// Bumped by every teardown that invalidates in-flight async work (the
	// amount→0 reset, disconnectedCallback) — captured at the start of each
	// async attempt (initializeExpressPayments, initializeCardField,
	// runInitializePaymentMethods, updatePaymentMethodsObservable) and
	// re-checked via attemptCurrent() after every await before touching
	// slot$/mounted$/instance fields, since unsubscribing the RxJS pipeline
	// wrapped around these plain async chains never cancels the promise
	// itself — it keeps running and would otherwise paint a fully-rendered
	// button bound to an order the reset already abandoned.
	private attemptEpoch = 0

	// Card submission is a single terminal-callback race: at most one of
	// onSuccess/onError/onCancel may act per submission. Reset when a new
	// submission starts (handleCardSubmit).
	private cardTerminalHandled = false

	// Refs for manual DOM updates
	private busyOverlayContainer: Ref<HTMLDivElement> = createRef()
	private paymentMethodsContainer: Ref<HTMLDivElement> = createRef()
	private submitButton: Ref<SchmancyButton> = createRef()
	private alert: Ref<SchmancyAlert> = createRef()

	// Payment instances (typed per Revolut SDK)
	private paymentRequestInstance: RevolutPaymentRequestInstance | null = null
	private revolutPayInstance: RevolutPayInstance | null = null
	private cardFieldInstance: RevolutCardFieldInstance | null = null

	// The amount/currency pair the CURRENTLY mounted instances are actually
	// built against — advances only when a rebuild attempt fully succeeds for
	// the value it targeted (runInitializePaymentMethods on boot;
	// updatePaymentMethodsObservable's success path on a rebuild — never its
	// catchError, and never when any express slot kept a stale instance after
	// a failed double-buffer attempt). This is the ONE source of truth
	// silentRebuildActive$ derives from below — a rebuild that fails, or that
	// only partially rebuilds, leaves this stale on purpose, which is what
	// keeps the guard engaged until a later attempt actually catches up.
	private mounted$ = new BehaviorSubject<{ amount: number | null; currency: string | null }>({
		amount: null,
		currency: null,
	})
	// Fired at the end of every rebuild attempt (success, partial, or failed)
	// with the (amount, currency) that attempt targeted — NOT mounted$, since
	// a failed or partial attempt deliberately leaves mounted$ stale. The
	// rebuild leg only retries when LIVE amount/currency has moved on from
	// this value: a re-price dropped mid-attempt (the retrigger this exists
	// for) has genuinely moved; a value that merely failed to mount and
	// hasn't changed since must NOT immediately retry itself — that would be
	// an unbounded retry storm against a backend that keeps rejecting the
	// exact same request. Recovery from a failure comes from the NEXT
	// genuine amount/currency change (the debounced leg), not from this tick.
	private rebuildTick$ = new Subject<{ amount: number; currency: string }>()
	// Fired by updatePaymentMethodsObservable's own tap when a rebuild ends
	// kept-stale — exactly the case rebuildTick$'s filter above refuses to
	// retry, since live amount/currency hasn't moved on from what was just
	// attempted. Feeds the ONE bounded auto-retry leg in connectedCallback.
	private staleDivergence$ = new Subject<{ amount: number; currency: string }>()
	// The one (amount, currency) divergence that leg has already auto-retried
	// for — never more than once per divergence, or a value that keeps
	// failing would retry forever. Cleared on a genuine settle (mounted$
	// advances), a new-order reset, and disconnect.
	private staleRetriedFor: { amount: number; currency: string } | null = null

	// Reactive property streams for watching changes
	private amount$ = new BehaviorSubject<number>(0)
	private currency$ = new BehaviorSubject<string>('EUR')
	private merchantPublicKey$ = new BehaviorSubject<string>('')

	// RxJS State Machine - replaces local @state properties with reactive state
	private sdkReady$ = new BehaviorSubject<boolean>(false)
	// The one producer of buyer-visible failure. Typed by what the buyer can DO
	// (see SchmancyRevoluteFailure), never by the vendor's wording: a failure
	// with no move for the buyer never reaches here at all, and the vendor's own
	// string rides `detail` for telemetry, not for a screen.
	private notice$ = new BehaviorSubject<PaymentNotice | null>(null)
	private paymentMethodsInitialized$ = new BehaviorSubject<boolean>(false)
	private isSubmittingCard$ = new BehaviorSubject<boolean>(false)
	private validatingCard = false
	// A BehaviorSubject (not a plain field) — the guard-derivation combineLatest
	// below is the single derivation of silentRebuildActive$, so a success
	// arriving mid-rebuild re-runs it immediately and releases `inert` instead
	// of leaving it stuck for a rebuild the payment's own success just mooted.
	private paymentCompleted$ = new BehaviorSubject<boolean>(false)

	// UI State Machine - replaces @state properties with reactive manual DOM updates
	private loading$ = new BehaviorSubject<boolean>(false)
	private cardFieldReady$ = new BehaviorSubject<boolean>(false)
	// True for the whole window between a detected re-price and the silent
	// rebuild's conclusion — the single source of truth for both `inert` on
	// paymentMethodsContainer and the srAnnouncement text (see connectedCallback).
	private silentRebuildActive$ = new BehaviorSubject<boolean>(false)

	// Card validation state - for async validation via Revolut SDK
	private validationMessages$ = new BehaviorSubject<string[]>([])
	private validationResolver: ((messages: string[]) => void) | null = null

	// Every vendor onSuccess/onError/click callback (Apple/Google Pay, Revolut
	// Pay, card) funnels here — one subscription owns the error/tracking/dispatch fan-out.
	private sdkPaymentEvent$ = new Subject<SdkPaymentEvent>()

	// initializePaymentMethodsIfNeeded() feeds this; the exhaustMap gate below
	// (not the paymentMethodsInitialized$ read-then-write) is what makes two
	// near-simultaneous callers resolve to exactly one boot sequence.
	private bootRequest$ = new Subject<void>()

	// The card field's own order token, cached so a theme-scheme flip can remount
	// the SDK's iframe against the SAME order (a restyle, not a new checkout) —
	// see connectedCallback's resolvedScheme$ leg and mountCardField().
	private cardFieldOrderToken: string | null = null
	// Baseline for detecting a genuine scheme CHANGE (vs. the current-value replay
	// every fresh resolvedScheme$ subscriber receives, and any pre-mount hydration
	// tick) — null until the first observed value, per connectedCallback's tap.
	private lastKnownScheme: 'dark' | 'light' | null = null

	override connectedCallback() {
		super.connectedCallback()

		// Initialize property streams with current values
		this.amount$.next(this.amount)
		this.currency$.next(this.currency)
		this.merchantPublicKey$.next(this.merchantPublicKey)

		merge(
			merge(
				fromEvent(window, 'blur'),
				fromEvent(window, 'focus'),
				fromEvent(document, 'focusin', { capture: true }),
				fromEvent(document, 'focusout', { capture: true }),
			).pipe(
				debounceTime(0),
				tap(() => {
					// Focus in an opaque payment frame is reported as activeElement,
					// while Chromium can withhold :focus-within across that document.
					const active = this.shadowRoot?.activeElement
					for (const slot of [this.walletSlot, this.revolutPaySlot]) {
						slot.section.value?.toggleAttribute(
							'data-focused',
							document.hasFocus() && active instanceof HTMLIFrameElement && slot.section.value.contains(active),
						)
					}
				}),
			),
			this.cardOpenRequested$.pipe(
				exhaustMap(() =>
					defer(() => from(this.updateComplete)).pipe(
						switchMap(() => from(this.cardDisclosure.value?.updateComplete ?? Promise.resolve(true))),
						switchMap(() =>
							animationFrames().pipe(
								filter(() => (this.cardFieldContainer.value?.getBoundingClientRect().width ?? 0) > 0),
								take(1),
								timeout({ first: 5000 }),
							),
						),
						switchMap(() => this.sdkReady$.pipe(filter(Boolean), take(1))),
						switchMap(() => (this.cardExpanded ? from(this.initializeCardField()) : EMPTY)),
						catchError(error => {
							this.noticeMethod = 'card'
							// schmancy-text-literal-ok: layout/init failure detail is telemetry only
							this.notice$.next({
								failure: 'blocked',
								detail: error instanceof Error ? error.message : 'Card form could not open',
							})
							return EMPTY
						}),
					),
				),
			),
			// Busy overlay + payment-methods opacity (no layout shift). loading$
			// only ever goes true during the initial boot sequence
			// (runInitializePaymentMethods) — a silent re-price rebuild
			// (updatePaymentMethodsObservable) never touches it, so no overlay or
			// dim appears mid-checkout; that path uses `inert` instead (see
			// paymentMethodsContainer below).
			this.loading$.pipe(
				tap(loading => {
					if (this.busyOverlayContainer.value) {
						this.busyOverlayContainer.value.style.opacity = loading ? '1' : '0'
						this.busyOverlayContainer.value.style.pointerEvents = loading ? 'auto' : 'none'
					}
					if (this.paymentMethodsContainer.value) {
						this.paymentMethodsContainer.value.style.opacity = loading ? '0.3' : '1'
					}
				}),
			),

			// Card slot skeleton↔iframe cross-fade — opacity only, the card slot's
			// reserved height never changes.
			this.cardFieldReady$.pipe(
				tap(ready => {
					if (this.cardFieldSkeleton.value && this.cardFieldContainer.value) {
						this.cardFieldSkeleton.value.style.opacity = ready ? '0' : '1'
						this.cardFieldContainer.value.style.opacity = ready ? '1' : '0'
					}
				}),
			),

			// Card field validity → wrapper chrome (border/ring color) AND the words
			// beside it. The chrome is the SAME shared class source the wrapper's
			// static chrome renders from at mount, re-flattened to a class-name
			// string for this imperative (ref-driven, not Lit-declarative) toggle;
			// the text is the half `<schmancy-input>` renders in its own
			// `hint-${id}` block, which a wrapper adopting only the class map would
			// otherwise drop — leaving a red border with no words and a
			// screen-reader user with silence. Both move together, from one source,
			// so neither can say something the other doesn't.
			this.validationMessages$.pipe(
				tap(messages => {
					if (this.cardFieldWrapper.value) {
						this.cardFieldWrapper.value.className = `relative w-full outline-none ${fieldChromeClassNames(messages.length > 0, true)}`
						this.cardFieldWrapper.value.setAttribute('aria-invalid', messages.length > 0 ? 'true' : 'false')
					}
					if (this.cardFieldHint.value) {
						this.cardFieldHint.value.textContent = messages.join(' ')
					}
					if (messages.length > 0 && this.cardDisclosureLabel) {
						this.cardExpanded = true
						if (this.cardDisclosure.value) this.cardDisclosure.value.open = true
					}
				}),
			),

			// Submitting-card → processing event for the host.
			this.isSubmittingCard$.pipe(
				tap(processing => {
					this.dispatchEvent(new SchmancyRevoluteProcessingEvent(processing))
				}),
			),

			// Submit button state depends on all four of the above — one
			// subscription, one read, instead of calling it from each of them.
			// notice$ is in here because the CTA is half of what a notice means:
			// any decline relabels it to its retry tense, `blocked` disables it.
			combineLatest([this.loading$, this.cardFieldReady$, this.isSubmittingCard$, this.notice$]).pipe(
				tap(() => this.updateSubmitButtonState()),
			),

			// Wallet slot (Apple/Google Pay) — this.walletSlot.render$() is the
			// slot's ONE DOM writer (section collapse, skeleton, and front/back
			// container opacity+pointerEvents, all derived together from one
			// SlotState — see ExpressSlot). Fires on the slot's boot-time
			// resolution, any later available↔unavailable flip, AND a silent
			// re-price rebuild's cross-fade (walletSlot.swap()) — one leg covers
			// every transition, so no separate write path can drift from it.
			this.walletSlot.render$(),

			// Revolut Pay slot — same shape as the wallet slot above.
			this.revolutPaySlot.render$(),

			// Notice → the inline alert in its reserved slot ABOVE the pay action.
			// Above, not below, for two reasons: the action is the last element of
			// the payment stack, so anything under it competes with the fold; and
			// reason-then-action is the reading order that makes sense of both.
			// The alert owns the whole raise choreography (spring open, announce,
			// hold the action in view, and blur and take focus only while focus is
			// on the pay action or on nothing) — this leg only says WHAT to raise.
			// `detail` is deliberately absent from the mapping: it
			// is telemetry and has no screen to reach. The tone splits on the same
			// pair the pay action's own disabled state does (see
			// updateSubmitButtonState): a decline of any kind is an error the buyer
			// just hit and can act on, while a widget that cannot take a payment at
			// all — no form behind the action (`blocked`) or no live order behind it
			// (`orderStale`) — is a warning about the surface itself. A host copy
			// object missing a key falls back to the wording table for THAT key
			// alone, never to a wordless panel.
			//
			// This map IS the resolution point for the widget's own words, and the
			// only one: the key is built from the failure member and read here, so
			// the panel carries whatever language `setComponentText` holds at the
			// moment the notice fires. Not per render — the alert is handed a
			// resolved object once and keeps it — so a swap mid-checkout reaches
			// the NEXT failure and leaves a panel already on screen in the outgoing
			// language, the same limit the pay action's own three labels carry.
			this.notice$.pipe(
				map((notice): SchmancyAlertNotice | null =>
					notice === null
						? null
						: {
								tone: notice.failure === 'blocked' || notice.failure === 'orderStale' ? 'warning' : 'error',
								...(this.noticeCopy?.[notice.failure] ?? {
									headline: componentText(`payment.notice.${notice.failure}.headline`),
									body: componentText(`payment.notice.${notice.failure}.body`),
									// Absent rather than present-and-empty for every other
									// member: the alert renders its action on `remedy !== undefined`.
									...(notice.failure === 'blocked' ? { remedy: componentText('payment.notice.blocked.remedy') } : {}),
								}),
							},
				),
				tap(raised => {
					if (raised === null) {
						this.alert.value?.clear()
						this.walletAlert.value?.clear()
						this.revolutPayAlert.value?.clear()
					} else if (this.cardDisclosureLabel && this.noticeMethod !== 'card') {
						this.alert.value?.clear()
						this.walletAlert.value?.clear()
						this.revolutPayAlert.value?.clear()
						if (this.noticeMethod === 'revolut-pay') this.revolutPayAlert.value?.raise(raised)
						else this.walletAlert.value?.raise(raised)
					} else {
						this.walletAlert.value?.clear()
						this.revolutPayAlert.value?.clear()
						if (this.cardDisclosureLabel) {
							this.cardExpanded = true
							if (this.cardDisclosure.value) this.cardDisclosure.value.open = true
						}
						this.alert.value?.raise(raised)
					}
				}),
			),

			// Vendor SDK payment events — the error/tracking/dispatch fan-out for
			// every payment method lives here, once.
			this.sdkPaymentEvent$.pipe(
				tap(event => {
					switch (event.kind) {
						case 'success':
							this.paymentCompleted$.next(true)
							this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_SUCCESS', event.method))
							this.dispatchEvent(new SchmancyRevoluteSuccessEvent({ token: event.token, timestamp: Date.now() }))
							break
						case 'error':
							this.noticeMethod = event.method
							// Every method's decline funnels here — card, Apple/Google Pay
							// and Revolut Pay alike, each already sorted at its own ingestion
							// point from the SDK's structured fields. The buyer asked for
							// money to move and it did not; which of those failures it was
							// decides only which words they read, never whether the action
							// stays live (see SchmancyRevoluteFailure).
							this.notice$.next({ failure: event.failure, detail: event.message })
							this.dispatchEvent(
								new SchmancyRevoluteTrackingEvent('PAYMENT_ERROR', event.method, { error: event.message }),
							)
							this.dispatchEvent(
								new SchmancyRevoluteErrorEvent({
									error: new Error(event.message),
									message: event.message,
									failure: event.failure,
								}),
							)
							break
						case 'click':
							this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_METHOD_CLICKED', event.method))
							break
					}
				}),
			),

			// Guards the mounted button whenever live amount/currency diverges
			// from mounted$ — undebounced and fully reactive (mounted$ and
			// paymentCompleted$, not plain fields, are combineLatest sources
			// here), so it engages the instant a re-price happens, well before
			// the rebuild leg below even starts, AND releases the instant live
			// catches back up with mounted$ OR a payment success arrives —
			// including a bounce that returns to the mounted value within the
			// rebuild leg's own 300ms debounce, before a rebuild ever fires.
			// `inert` (paired with the srAnnouncement text) is driven SOLELY by
			// this leg — updatePaymentMethodsObservable never writes
			// silentRebuildActive$ directly; it only ever advances mounted$, and
			// only when a rebuild attempt actually settles for the value it targeted.
			combineLatest([
				this.amount$,
				this.currency$,
				this.mounted$,
				this.paymentMethodsInitialized$,
				this.paymentCompleted$,
			]).pipe(
				map(
					([amount, currency, mounted, initialized, completed]) =>
						initialized && !completed && amount !== 0 && (amount !== mounted.amount || currency !== mounted.currency),
				),
				distinctUntilChanged(),
				tap(diverged => this.silentRebuildActive$.next(diverged)),
			),

			// Silent guard → `inert` + the aria-live announcement, one place so
			// the two can never drift out of sync.
			this.silentRebuildActive$.pipe(
				tap(active => {
					if (this.paymentMethodsContainer.value) {
						this.paymentMethodsContainer.value.inert = active
					}
					if (this.srAnnouncement.value) {
						this.srAnnouncement.value.textContent = active ? componentText('payment.announce.updating') : ''
					}
				}),
			),

			// A buyer mid-card-3DS-challenge (isSubmittingCard$) or post-success
			// (paymentCompleted$) must not be able to reach the OTHER payment
			// methods via keyboard — silentRebuildActive$'s inert above doesn't
			// cover this (a card submission isn't a rebuild), and the card
			// iframe itself must stay interactive throughout 3DS, so only the
			// two express-slot SECTIONS go inert here — never
			// paymentMethodsContainer (which would also inert the card iframe),
			// never the card wrapper.
			combineLatest([this.isSubmittingCard$, this.paymentCompleted$]).pipe(
				map(([submittingCard, completed]) => submittingCard || completed),
				distinctUntilChanged(),
				tap(blockExpress => {
					if (this.walletSlot.section.value) this.walletSlot.section.value.inert = blockExpress
					if (this.revolutPaySlot.section.value) this.revolutPaySlot.section.value.inert = blockExpress
				}),
			),

			// Amount/currency change → rebuild the mounted methods. Two sources
			// feed this leg: the debounced amount$/currency$ pair (a genuine user
			// edit) and rebuildTick$ (fires with the value the JUST-CONCLUDED
			// attempt targeted — see the field's own doc). rebuildTick$ is
			// filtered to only pass when LIVE amount/currency has moved on from
			// that target: this is what retries a re-price dropped mid-rebuild
			// (exhaustMap: a concurrent change while a rebuild is in flight is
			// dropped, not queued — rebuildTick$ re-checks once that rebuild
			// frees up), and what refuses to immediately retry a value that
			// merely failed to mount and hasn't changed since — that recovery
			// comes only from the debounced leg's own next genuine change.
			// Deliberately no distinctUntilChanged ahead of exhaustMap here: it
			// would mark a value "seen" even on an emission exhaustMap itself
			// drops for being busy, wrongly suppressing a later legitimate
			// re-check of that same value. The mounted$-equality short-circuit
			// below is what skips a redundant rebuild instead — reached
			// whenever live has bounced back to (or converges onto) an
			// already-settled value.
			//
			// A third source, staleDivergence$, compensates for the ONE case
			// rebuildTick$'s own filter above deliberately refuses: a rebuild
			// that left a slot kept-stale (initializeExpressPayments' doc) at an
			// UNCHANGED amount/currency. updatePaymentMethodsObservable's tap
			// fires staleDivergence$ for that case; this leg waits
			// STALE_RETRY_DELAY_MS, re-checks the divergence is still live and
			// still unresolved, and feeds it into the SAME rebuild pipeline —
			// bounded to exactly one attempt per divergence (staleRetriedFor),
			// after which only a genuine next amount/currency change recovers.
			merge(
				combineLatest([this.amount$, this.currency$]).pipe(debounceTime(300)),
				this.rebuildTick$.pipe(
					filter(justAttempted => this.amount !== justAttempted.amount || this.currency !== justAttempted.currency),
					map(() => [this.amount, this.currency] as [number, string]),
				),
				this.staleDivergence$.pipe(
					filter(
						divergence =>
							this.staleRetriedFor?.amount !== divergence.amount ||
							this.staleRetriedFor?.currency !== divergence.currency,
					),
					tap(divergence => {
						this.staleRetriedFor = divergence
					}),
					switchMap(divergence => timer(STALE_RETRY_DELAY_MS).pipe(map(() => divergence))),
					filter(divergence => this.amount === divergence.amount && this.currency === divergence.currency),
					map(divergence => [divergence.amount, divergence.currency] as [number, string]),
				),
			).pipe(
				exhaustMap(([amount, currency]) => {
					if (!this.paymentMethodsInitialized$.value || amount === 0) {
						return EMPTY
					}
					if (amount === this.mounted$.value.amount && currency === this.mounted$.value.currency) {
						return EMPTY
					}
					return this.updatePaymentMethodsObservable(amount, currency)
				}),
			),

			// Auto-init once amount and merchant key are both ready — component is
			// independent of any parent-driven boot call.
			combineLatest([this.amount$, this.merchantPublicKey$]).pipe(
				filter(([amount, key]) => amount > 0 && !!key),
				tap(() => this.initializePaymentMethodsIfNeeded()),
			),

			// Auto-reset when amount goes to 0 (cart cleared) — allows re-initialization.
			this.amount$.pipe(
				distinctUntilChanged(),
				filter(amount => amount === 0),
				tap(() => {
					this.emailError = ''
					this.paymentMethodsInitialized$.next(false)
					this.cardFieldReady$.next(false)
					// Back to 'pending'/front-A, both containers explicitly cleared —
					// ExpressSlot.reset() owns this (see its doc); the next init
					// cycle's own innerHTML clear + mount runs behind the skeleton,
					// and the cross-fade hides whatever instance the destroy below
					// tore down.
					this.walletSlot.reset()
					this.revolutPaySlot.reset()
					this.notice$.next(null)
					this.loading$.next(false)
					this.isSubmittingCard$.next(false)
					this.cardFieldOrderToken = null
					// amount === 0 already gates the guard leg's own diverged$
					// derivation off, so silentRebuildActive$ releases on its own;
					// resetting mounted$/staleRetriedFor here is hygiene for the next
					// boot cycle.
					this.mounted$.next({ amount: null, currency: null })
					this.staleRetriedFor = null
					this.paymentCompleted$.next(false)
					this.destroyExpressInstances()
					this.destroyCardFieldInstance()
					// Invalidates any in-flight initializeExpressPayments/
					// initializeCardField/updatePaymentMethodsObservable attempt still
					// awaiting a vendor call above — its eventual result is discarded
					// (see attemptCurrent()) instead of painting a stale button over
					// the order this reset just abandoned.
					this.attemptEpoch++
				}),
			),

			// Boot request → the actual init sequence. exhaustMap — not the
			// paymentMethodsInitialized$ read below — is what makes two
			// near-simultaneous callers (the auto-init leg above, and any direct
			// parent call) resolve to exactly one run: both may pass the filter
			// before either flips the flag, but exhaustMap drops the second
			// because the first is still in flight.
			this.bootRequest$.pipe(
				filter(() => !this.paymentMethodsInitialized$.value),
				exhaustMap(() => this.runInitializePaymentMethods()),
			),

			// Theme scheme (dark/light) → re-resolve the SDK card field's snapshot
			// colors. The Revolut card-field instance exposes no restyle/update API
			// (createCardField's constructor options are the only style entry point),
			// so a genuine scheme flip is the one legitimate remount: destroy + recreate
			// against the SAME cached order token (never a new requestOrderFromParent()
			// round-trip — this is a restyle, not a new checkout), behind the SAME
			// skeleton cross-fade every other card-field mount already uses. No height
			// lock is needed — the wrapper's height is fixed (CONTROL_SIZE-derived, not
			// content-derived), so nothing can shift. A theme flip is a rare explicit
			// user act, not a routine remount path; `lastKnownScheme` exists solely to
			// ignore the replayed current scheme at subscribe time and any pre-mount
			// hydration tick, neither of which is a flip. A flip mid-submit defers to
			// the FIRST moment isSubmittingCard$ settles — destroying the instance
			// while the SDK's onSuccess/onError/onCancel callbacks are still owed for
			// an in-flight submission would orphan that gate instead of releasing it.
			theme.resolvedScheme$.pipe(
				map(scheme => ({
					isFlip: this.lastKnownScheme !== null && this.lastKnownScheme !== scheme && !!this.cardFieldInstance,
					scheme,
				})),
				tap(({ scheme }) => {
					this.lastKnownScheme = scheme
				}),
				filter(({ isFlip }) => isFlip),
				switchMap(() =>
					this.isSubmittingCard$.pipe(
						filter(submitting => !submitting),
						take(1),
					),
				),
				tap(() => {
					if (!this.cardFieldOrderToken) return
					this.destroyCardFieldInstance()
					this.cardFieldReady$.next(false)
					try {
						this.mountCardField(this.cardFieldOrderToken)
					} catch (error) {
						// A theme flip could not remount the iframe. The form still works;
						// only its colours are stale. The buyer has no move here and no
						// stake in it — a warning, never a notice.
						console.warn('[SchmancyRevolute] Card field theme-remount failed:', error)
					}
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	/** Every SDK instance (payment request, Revolut Pay, card field) exposes the
	 * same `destroy(): void` shape; this is the one place that calls it inside
	 * a try/catch, since an instance already torn down (or never fully
	 * mounted) can throw on a second destroy. */
	private safeDestroy(instance: { destroy: () => void } | null | undefined): void {
		try {
			instance?.destroy?.()
		} catch {
			// Ignore destroy errors — instance may already be torn down.
		}
	}

	/**
	 * Every vendor SDK await (and the host's own order-token promise, via
	 * requestOrderFromParent) races this bound so a promise that never
	 * settles can't pin loading$/inert/the exhaustMap gate forever — a
	 * timeout throws exactly like any other vendor exception, so every
	 * caller's own existing catchError/finalize path releases the gate it
	 * already releases for a genuine SDK error, with no extra plumbing.
	 */
	private withVendorTimeout<T>(promise: T | Promise<T>, label: string): Promise<T> {
		// Promise.resolve, not a bare from(): the live embed.js returns some of
		// these results synchronously (no .then), which `await` tolerated but
		// rxjs `from()` rejects outright — assimilation covers both shapes.
		return firstValueFrom(
			race(
				from(Promise.resolve(promise)),
				timer(VENDOR_AWAIT_TIMEOUT_MS).pipe(
					map(() => {
						throw new Error(`${label} timed out`)
					}),
				),
			),
		)
	}

	/** True when `epoch` (captured at an async attempt's own start) still
	 * matches the live attemptEpoch — false means a reset or disconnect
	 * invalidated this attempt while it was awaiting a vendor call, and its
	 * result must be discarded without writing slot$/mounted$/instance state. */
	private attemptCurrent(epoch: number): boolean {
		return this.attemptEpoch === epoch
	}

	/**
	 * Full express-payment teardown — a new-order reset and disconnect. A
	 * silent re-price rebuild destroys each old instance individually, at its
	 * own double-buffer swap point (see initializeExpressPayments), never both
	 * at once via this method.
	 */
	private destroyExpressInstances(): void {
		this.safeDestroy(this.paymentRequestInstance)
		this.paymentRequestInstance = null
		this.safeDestroy(this.revolutPayInstance)
		this.revolutPayInstance = null
	}

	/**
	 * Torn down on a new-order reset, a theme-scheme flip remount, a silent
	 * re-price rebuild (the card slot keeps its skeleton cross-fade rather
	 * than double-buffering — see updatePaymentMethodsObservable), and disconnect.
	 */
	private destroyCardFieldInstance(): void {
		this.safeDestroy(this.cardFieldInstance)
		this.cardFieldInstance = null
	}

	/**
	 * Update submit button state via manual DOM manipulation
	 * Called by subscriptions when relevant state changes
	 */
	private updateSubmitButtonState() {
		if (this.cardDisclosure.value)
			this.cardDisclosure.value.locked = this.validatingCard || this.isSubmittingCard$.value || this.settling
		if (this.submitButton.value) {
			const failure = this.notice$.value?.failure
			// The two members for which pressing this button could not possibly
			// take a payment: `blocked` has no form behind it, `orderStale` has no
			// live order behind it. Leaving either one live would let the buyer
			// press a promise the widget cannot keep, and relabelling to the retry
			// tense would name a move that cannot work — so both lines below test
			// this pair, never a particular decline member. Every OTHER member,
			// whatever the bank's reason, leaves form and order intact, so retrying
			// or a different card is exactly the move and the label says so.
			// `blocked`'s remedy is inside the alert; `orderStale`'s belongs to the
			// host, which learns of it from `schmancy-revolute-error`.
			const cannotSubmit = failure === 'blocked' || failure === 'orderStale'
			const disabled = this.submitDisabled || cannotSubmit || !this.cardFieldReady$.value || this.loading$.value

			this.submitButton.value.toggleAttribute('disabled', disabled)
			this.submitButton.value.busy = this.validatingCard || this.isSubmittingCard$.value || this.settling

			// Update button text content. This method is the button's ONLY author —
			// the template leaves it empty on purpose — so every state the buyer can
			// read is written from here, whether its wording comes from a host prop
			// or from the library's own table.
			const textContent =
				this.submitButton.value.busy && this.submitButton.value.textContent
					? this.submitButton.value.textContent
					: failure && !cannotSubmit
						? this.retryPayLabel || componentText('payment.retry')
						: this.payLabel || componentText('payment.pay')
			if (this.submitButton.value.textContent !== textContent) {
				this.submitButton.value.textContent = textContent
			}
		}
	}

	override firstUpdated(changedProperties: PropertyValues) {
		super.firstUpdated(changedProperties)
		// CRITICAL FIX: Don't load SDK here - sandboxMode may not be set yet.
		// SDK will be loaded in initializePaymentMethodsIfNeeded() when all props
		// are ready. This ensures sandboxMode is set before SDK loads.

		// The alert opens directly above the pay action and scrolls it back to the
		// bottom edge once its spring settles. Assigned here rather than bound in
		// the template because `submitButton`'s ref resolves during the same render
		// a `.anchor=${this.submitButton.value}` binding would read it in, and
		// nothing re-renders this widget on a ref resolving — its own DOM is
		// ref-driven, so a second render is not guaranteed to come at all. The
		// alert reads `anchor` at settle time, long after this.
		if (this.alert.value) this.alert.value.anchor = this.submitButton.value

		// The submit button carries no text in the template — updateSubmitButtonState
		// is its one author, and the label it writes depends on state no first render
		// can know. Called here, in the same update cycle as the commit that resolved
		// the ref, so the first painted frame already carries the real label; a
		// template placeholder would have to spell one of those labels a second time
		// and could then disagree with this one.
		this.updateSubmitButtonState()
	}

	override updated(changedProperties: PropertyValues) {
		super.updated(changedProperties)

		// Update BehaviorSubjects when properties change
		if (changedProperties.has('amount')) {
			this.amount$.next(this.amount)
		}
		if (changedProperties.has('currency')) {
			this.currency$.next(this.currency)
		}
		if (changedProperties.has('merchantPublicKey')) {
			this.merchantPublicKey$.next(this.merchantPublicKey)
		}
		// The submit button is imperatively DOM-driven (see updateSubmitButtonState) —
		// a plain @property change on submitDisabled/payLabel/retryPayLabel doesn't
		// re-run it on its own.
		if (
			changedProperties.has('submitDisabled') ||
			changedProperties.has('settling') ||
			changedProperties.has('payLabel') ||
			changedProperties.has('retryPayLabel')
		) {
			this.updateSubmitButtonState()
		}
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.destroyExpressInstances()
		this.destroyCardFieldInstance()
		this.cardFieldOrderToken = null
		this.lastKnownScheme = null
		this.paymentCompleted$.next(false)
		this.validationResolver = null
		this.mounted$.next({ amount: null, currency: null })
		this.staleRetriedFor = null
		// Invalidates any in-flight attempt still awaiting a vendor call — up to
		// VENDOR_AWAIT_TIMEOUT_MS later, its result is discarded (attemptCurrent())
		// rather than painting a fully-rendered stale button over a dead session.
		this.attemptEpoch++
		// A same-instance DOM move (schmancy's own pinned reparent contract,
		// reparent.test.ts) tears down the SDK instances above but must ALSO
		// reset the slot machines and the gates they feed — otherwise a
		// reconnect's fresh connectedCallback resubscribes render$() against a
		// BehaviorSubject still holding 'available'/ready from the dead
		// instances, replaying those visuals over nothing, and the card field
		// can never remount (cardFieldReady$ stuck true with no instance
		// behind it). Resetting here lets the reconnect re-boot cleanly via
		// the existing auto-init leg (amount + merchantPublicKey already ready).
		this.walletSlot.reset()
		this.revolutPaySlot.reset()
		this.cardFieldReady$.next(false)
		this.paymentMethodsInitialized$.next(false)
	}

	/**
	 * True when this configuration can never resolve the Apple/Google Pay wallet
	 * slot — no merchant key (express payments never attempt at all) or both
	 * wallet-enabling props are off. Mirrors the exact condition
	 * initializeExpressPayments() gates the SDK attempt on, so "impossible" has
	 * one definition.
	 */
	private walletSlotImpossible(): boolean {
		return !this.merchantPublicKey || (!this.enableApplePay && !this.enableGooglePay)
	}

	/** Revolut Pay analogue of walletSlotImpossible(). */
	private revolutPaySlotImpossible(): boolean {
		return !this.merchantPublicKey || !this.enableRevolutPay
	}

	/**
	 * Initialize payment methods (Card Field, Apple/Google Pay, Revolut Pay)
	 * CRITICAL: This creates an order in the database, so only call when user is ready to pay
	 * Called by parent component when checkout is visible and cart has items.
	 * Idempotent — see bootRequest$'s exhaustMap gate in connectedCallback.
	 */
	initializePaymentMethodsIfNeeded() {
		// Collapse a slot the config already rules out BEFORE the SDK-load/order-
		// creation wait below, not after — reserve-until-resolved is for a slot
		// with a genuine pending question; a disabled/impossible slot has none,
		// so its skeleton never shows even during boot.
		if (this.walletSlotImpossible()) this.walletSlot.resolve('unavailable')
		if (this.revolutPaySlotImpossible()) this.revolutPaySlot.resolve('unavailable')
		this.bootRequest$.next()
	}

	/**
	 * The actual boot sequence — SDK load, order creation, and mount — reached
	 * only via bootRequest$'s gate in connectedCallback.
	 */
	private runInitializePaymentMethods(): Observable<void> {
		// Captured at boot's own start, both for the epoch guard below (see
		// attemptEpoch's doc) and so the final mounted$ stamp reflects what
		// THIS boot actually targeted rather than whatever `this.amount`/
		// `this.currency` happen to be by the time the async chain settles.
		const epoch = this.attemptEpoch
		const bootTarget = { amount: this.amount, currency: this.currency }
		return of(null).pipe(
			tap(() => {
				this.loading$.next(true)
				this.notice$.next(null)
			}),
			// Wait for component to render refs
			switchMap(() => from(this.updateComplete)),
			// Load SDK if not already loaded (ensures sandboxMode is set before SDK
			// loads — this is why firstUpdated never loads it). Retry is scoped to
			// this leg alone: it must never replay the order creation below.
			switchMap(() => {
				if (this.sdkReady$.value) {
					return of(null)
				}
				return defer(() => loadRevolutSdkOnce(this.sandboxMode)).pipe(
					switchMap(() => {
						if (window.RevolutCheckout) {
							return of(null)
						}
						// Fixed 100ms poll, ~5s timeout — not exponential backoff.
						const maxChecks = 51
						return timer(0, 100).pipe(
							take(maxChecks),
							map(() => !!window.RevolutCheckout),
							first(ready => ready, false),
							switchMap(ready => (ready ? of(null) : throwError(() => new Error('Revolut SDK load timeout')))),
						)
					}),
					tap(() => {
						this.sdkReady$.next(true)
					}),
					catchError(error => {
						console.error('[SchmancyRevolute] SDK loading failed:', error)
						throw error // Re-throw to trigger retry
					}),
					// Linear backoff, 200ms steps — not exponential.
					retry({ count: 5, delay: (_err, retryCount) => timer(retryCount * 200) }),
				)
			}),
			// Initialize payment methods — deliberately outside the retry above: a
			// transient mount failure must never re-fire order creation.
			switchMap(() => from(this.initializePaymentMethods())),
			tap(() => {
				if (!this.attemptCurrent(epoch)) return // reset/disconnect invalidated this boot already
				this.paymentMethodsInitialized$.next(true)
				this.mounted$.next(bootTarget)
				this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_VIEW'))
			}),
			catchError(error => {
				if (!this.attemptCurrent(epoch)) return EMPTY // an abandoned boot's own failure isn't worth surfacing
				console.error('[SchmancyRevolute] Initialization failed:', error)
				const message = error instanceof Error ? error.message : String(error)
				// SDK load timeout, order creation failure, or a card-field mount
				// that threw. Nothing was mounted, so there is nothing to submit and
				// the pay button would be a lie — the alert carries the remedy.
				// schmancy-text-literal-ok: boot-failure stand-in on PaymentNotice.detail — telemetry, never rendered
				this.notice$.next({ failure: 'blocked', detail: message || 'Failed to initialize payment methods' })
				this.dispatchEvent(
					new SchmancyRevoluteErrorEvent({
						error: error instanceof Error ? error : new Error(message),
						// schmancy-text-literal-ok: boot-failure stand-in on the error event's message — the host's log payload
						message: message || 'Failed to initialize payment methods',
						failure: 'blocked',
					}),
				)
				return EMPTY
			}),
			finalize(() => {
				// A LATER boot's own loading$.next(true) must not be clobbered by
				// this abandoned attempt settling after it — see attemptEpoch's doc.
				if (this.attemptCurrent(epoch)) this.loading$.next(false)
			}),
		)
	}

	/**
	 * Request order creation from parent component (event-driven pattern)
	 * Returns a Promise that resolves when parent provides the order token —
	 * raced against withVendorTimeout, since a host that never resolves nor
	 * rejects the event's promise is the same hang-forever hazard as a bare
	 * vendor SDK await.
	 *
	 * Note: Backend returns 'token', SDK expects 'publicId' - they're the same value.
	 * The mapping from token → publicId happens at the SDK boundary (createOrder callbacks).
	 */
	private requestOrderFromParent(): Promise<{ token: string }> {
		const orderRequested = new Promise<{ token: string }>((resolve, reject) => {
			const event = new SchmancyRevoluteRequestOrderEvent({
				resolve,
				reject,
				email: this.email ?? this.emailInput?.trim() ?? '',
			})
			this.dispatchEvent(event)
		})
		return this.withVendorTimeout(orderRequested, 'the host order request')
	}

	/**
	 * Two initialization paths: with a merchant public key → express payments
	 * (Apple/Google/Revolut Pay) + card field; without one → card field only.
	 * Documentation: https://developer.revolut.com/docs/sdks/merchant-web-sdk/payment-methods/introduction
	 */
	private async initializePaymentMethods(): Promise<void> {
		if (this.merchantPublicKey) {
			await this.initializeExpressPayments('boot')
			// Skip card field if payment already completed (race condition guard).
			if (this.paymentCompleted$.value) {
				return
			}
			await this.initializeCardField()
			return
		}

		// No merchant public key → express payments never attempt at all;
		// resolve both wallet slots definitively so their skeletons don't wait
		// forever, then collapse them (no reserved space for a mode this
		// integration was never configured to offer).
		this.walletSlot.resolve('unavailable')
		this.revolutPaySlot.resolve('unavailable')
		await this.initializeCardField()
	}

	/**
	 * Revolut Merchant API - Express Payments (Apple Pay, Google Pay, Revolut Pay)
	 * Official Documentation: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/apple-pay-google-pay/web
	 *
	 * Key Concepts:
	 * - Uses Merchant Public API Key (different from order token)
	 * - Supports Apple Pay, Google Pay, and Revolut Pay
	 * - Payment Request API for Apple Pay / Google Pay
	 * - Revolut Pay has its own dedicated API
	 *
	 * Documentation References:
	 * - Apple Pay/Google Pay: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/apple-pay-google-pay/web#step-2-initialize-revolut-checkout
	 * - Revolut Pay: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/revolut-pay/web
	 *
	 * `mode: 'boot'` mounts each slot directly into its front container — the
	 * slot is still 'pending' with its skeleton showing, so there's nothing to
	 * cross-fade against. `mode: 'rebuild'` (a silent re-price) mounts a fresh
	 * instance into the front container's still-empty counterpart ONLY when
	 * that slot is currently 'available' — the live button stays painted,
	 * `inert`-blocked, until the fresh instance confirms itself
	 * (canMakePayment+render for the wallet slot, mount for Revolut Pay), at
	 * which point the two cross-fade and the old instance is destroyed. A
	 * slot that wasn't 'available' going in (impossible, or a previous
	 * attempt failed) is re-attempted straight into its front container, same
	 * as boot — there is no live button to preserve.
	 *
	 * A rebuild's fresh-mount outcome that isn't success splits in two: a
	 * DEFINITIVE ineligibility answer (Apple/Google Pay's canMakePayment
	 * resolving cleanly false — an eligibility fact, not a technical failure)
	 * collapses the slot exactly as a first-boot failure would, discarding
	 * both the fresh and the old instance. Any other failure (a thrown
	 * exception, or — for Revolut Pay, whose SDK exposes no separate
	 * eligibility check — any mount failure at all) is a MERE RE-PRICE
	 * failure: the still-valid old instance stays painted and the slot stays
	 * 'available', since destroying a working button and animating a visible
	 * collapse over a transient re-price error would itself be the flicker
	 * this whole mechanism exists to prevent. The old instance is mounted
	 * against a stale amount, so it must not become tappable again — the
	 * caller never advances mounted$ when this method reports a kept-stale
	 * slot, which is what keeps the guard (`inert`) engaged. The failure never
	 * reaches the buyer: the widget auto-retries it (staleDivergence$) and the
	 * host's own settling pay label already carries the wait, so a notice would
	 * be a second, louder account of something already being handled.
	 *
	 * Returns false if either express slot kept a stale instance from a
	 * rebuild-mode failure — the caller's signal that mounted$ must NOT
	 * advance to the value this attempt targeted, since a stale slot means
	 * this attempt did not, in fact, fully rebuild against it.
	 */
	private async initializeExpressPayments(mode: 'boot' | 'rebuild'): Promise<boolean> {
		// Captured once, at this attempt's own start — every await below
		// re-checks it via attemptCurrent() before touching slot$/instance
		// state, since a reset or disconnect racing this promise chain never
		// cancels it (see attemptEpoch's own doc).
		const epoch = this.attemptEpoch

		// PRE-CREATE ORDER for express payments (Apple Pay / Google Pay / Revolut Pay)
		// CRITICAL: createOrder callback must return INSTANTLY to maintain browser "user activation"
		// If it takes too long, browser blocks payment sheet with "show() must be triggered by user activation"
		// `token` is captured by each instance's own createOrder closure below,
		// never read off a shared mutable field — a double-buffered rebuild's
		// fresh instances mount against a NEW token while the still-painted old
		// instance (its own closure) must keep resolving the token it was born
		// with, unaffected by whatever this or a later call does afterward.
		// requestOrderFromParent() already races withVendorTimeout internally.
		const { token } = await this.requestOrderFromParent()
		if (!this.attemptCurrent(epoch)) return false // abandoned — nothing mounted yet to destroy

		// Initialize RevolutCheckout.payments() with Merchant Public API Key
		// Documentation: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/apple-pay-google-pay/web#step-2-initialize-revolut-checkout
		const { revolutPay, paymentRequest } = await this.withVendorTimeout(
			window.RevolutCheckout.payments({
				publicToken: this.merchantPublicKey, // Merchant Public API Key from settings
				locale: 'en',
			}),
			'RevolutCheckout.payments()',
		)
		if (!this.attemptCurrent(epoch)) return false // still nothing mounted — payments() only resolves method handles

		// Initialize Apple Pay / Google Pay
		// Documentation: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/apple-pay-google-pay/web#step-3-add-payment-request-buttons
		// True only when a rebuild-mode double-buffer attempt failed and left
		// the old, still-valid instance painted — the caller's signal that
		// mounted$ must not advance for this attempt (see the method doc above).
		let walletKeptStale = false
		const walletWasAvailable = this.walletSlot.slot$.value.status === 'available'
		const walletDoubleBuffer = mode === 'rebuild' && walletWasAvailable
		const walletTarget = walletDoubleBuffer ? this.walletSlot.backEl : this.walletSlot.frontEl

		if ((this.enableApplePay || this.enableGooglePay) && walletTarget) {
			if (!walletDoubleBuffer) {
				// Clear container DOM to prevent duplicate buttons on reinit
				walletTarget.innerHTML = ''
			}
			const previousInstance = walletDoubleBuffer ? this.paymentRequestInstance : null

			const freshInstance = paymentRequest(walletTarget, {
				currency: this.currency,
				amount: this.amount,
				// SDK expects { publicId }, we use pre-created token for instant response
				// CRITICAL: Must return instantly to maintain browser "user activation"
				createOrder: () => Promise.resolve({ publicId: token }),
				buttonStyle: walletButtonStyle(),
				onSuccess: () => this.sdkPaymentEvent$.next({ kind: 'success', method: 'apple-pay' }),
				onError: (error: RevolutCheckoutError) =>
					this.sdkPaymentEvent$.next({
						kind: 'error',
						method: 'apple-pay',
						failure: classifyRevoluteFailure(error.type, error.code, 'apple-pay'),
						// schmancy-text-literal-ok: Apple/Google Pay vendor-error stand-in, carried to PaymentNotice.detail
						message: error.message || 'Payment failed',
					}),
				requestPayerInfo: {
					phone: false,
				},
			})

			// Check if Apple Pay / Google Pay is available on this device.
			// definitivelyIneligible distinguishes a clean canMakePayment "no"
			// (an eligibility fact) from any thrown exception (canMakePayment or
			// render() failing outright — a technical failure, not an answer) —
			// including a withVendorTimeout timeout, which is treated the same
			// as any other technical failure (not an eligibility answer).
			let available = false
			let definitivelyIneligible = false
			try {
				const sdkMethod = await this.withVendorTimeout(freshInstance.canMakePayment(), 'canMakePayment()')
				if (sdkMethod) {
					// Convert SDK method name to our PaymentMethod type
					const method: PaymentMethod = sdkMethod === 'applePay' ? 'apple-pay' : 'google-pay'
					this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_METHOD_AVAILABLE', method))
					await this.withVendorTimeout(freshInstance.render(), 'render()')
					available = true
				} else {
					definitivelyIneligible = true
				}
			} catch {
				// Don't throw - just mark as unavailable and continue with other payment methods
				available = false
				definitivelyIneligible = false
			}

			if (!this.attemptCurrent(epoch)) {
				// A reset/disconnect invalidated this attempt during canMakePayment()/
				// render() — the freshly-rendered button is bound to an abandoned
				// order; destroy it and abandon the whole attempt, revolutPay included.
				this.safeDestroy(freshInstance)
				return false
			}

			if (available) {
				this.paymentRequestInstance = freshInstance
				if (walletDoubleBuffer) {
					this.walletSlot.swap()
					await firstValueFrom(timer(150))
					this.safeDestroy(previousInstance)
				} else {
					this.walletSlot.resolve('available')
				}
			} else if (walletDoubleBuffer && !definitivelyIneligible) {
				this.safeDestroy(freshInstance)
				walletKeptStale = true
				// No notice from here, whichever attempt this is: the buyer-facing half
				// of a kept-stale slot is decided once, where the retry budget lives
				// (updatePaymentMethodsObservable's kept-stale branch), rather than
				// per slot — two slots keeping stale in one attempt is still one
				// thing that happened to the buyer.
				const message = 'Failed to refresh Apple Pay / Google Pay for the updated amount'
				console.warn(`[SchmancyRevolute] ${message}`)
				this.dispatchEvent(new SchmancyRevoluteErrorEvent({ error: new Error(message), message }))
			} else {
				this.safeDestroy(freshInstance)
				this.safeDestroy(previousInstance)
				if (walletDoubleBuffer) this.paymentRequestInstance = null
				this.walletSlot.resolve('unavailable')
			}
		} else {
			// Never attempted (feature disabled, or the ref isn't there) — resolve
			// definitively now rather than leaving the reserved skeleton pending forever.
			this.walletSlot.resolve('unavailable')
		}

		if (!this.attemptCurrent(epoch)) return false // abandoned during the wallet section's own async work (the double-buffer swap's timer(150))

		// Initialize Revolut Pay
		// Documentation: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/revolut-pay/web
		// Same kept-stale signal as the wallet slot above.
		let revolutKeptStale = false
		const revolutWasAvailable = this.revolutPaySlot.slot$.value.status === 'available'
		const revolutDoubleBuffer = mode === 'rebuild' && revolutWasAvailable
		const revolutTarget = revolutDoubleBuffer ? this.revolutPaySlot.backEl : this.revolutPaySlot.frontEl

		if (this.enableRevolutPay && revolutTarget) {
			if (!revolutDoubleBuffer) {
				// Clear container DOM to prevent duplicate buttons on reinit
				revolutTarget.innerHTML = ''
			}
			const previousInstance = revolutDoubleBuffer ? this.revolutPayInstance : null

			// Documentation: https://developer.revolut.com/docs/sdks/merchant-web-sdk/payment-methods/revolut-pay
			// Build mount options with strict typing per Revolut SDK
			const revolutPayOptions: RevolutPayMountOptions = {
				currency: this.currency,
				totalAmount: this.amount,
				// SDK expects { publicId }, we use pre-created token for instant response
				// CRITICAL: Must return instantly to maintain browser "user activation"
				createOrder: () => Promise.resolve({ publicId: token }),
				buttonStyle: {
					...walletButtonStyle(),
					cashback: false, // Hide "Earn 2x RevPoints" promotional message
				},
				// Add redirectUrls if provided (required for mobile flows per Revolut docs)
				// SDK appends order ID as ?_rp_oid=<order_id> to the URL
				// Separate URLs for success, failure, and cancel distinguish outcomes
				...(this.redirectUrls && { redirectUrls: this.redirectUrls }),
			}

			// Mount failure (no different in kind from canMakePayment above) marks
			// this method unavailable and lets Apple/Google Pay and card field proceed —
			// never throws out of initializeExpressPayments for a single method's failure.
			// withVendorTimeout's timeout surfaces here exactly like any other
			// mount rejection.
			try {
				await this.withVendorTimeout(revolutPay.mount(revolutTarget, revolutPayOptions), 'revolutPay.mount()')
				if (!this.attemptCurrent(epoch)) {
					this.safeDestroy(revolutPay)
					return false
				}
				this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_METHOD_AVAILABLE', 'revolut-pay'))

				revolutPay.on('payment', (event: RevolutPayEvent) => {
					if (event.type === 'click') {
						this.sdkPaymentEvent$.next({ kind: 'click', method: 'revolut-pay' })
					} else if (event.type === 'success') {
						this.sdkPaymentEvent$.next({
							kind: 'success',
							method: 'revolut-pay',
							// SDK returns orderId (newer) or publicId (legacy) - both are the token
							token: event.orderId || event.publicId,
						})
					} else if (event.type === 'error') {
						this.sdkPaymentEvent$.next({
							kind: 'error',
							method: 'revolut-pay',
							failure: classifyRevoluteFailure(event.error?.type, event.error?.code, 'revolut-pay'),
							// schmancy-text-literal-ok: Revolut Pay vendor-error stand-in, carried to PaymentNotice.detail
							message: event.error?.message || 'Payment failed',
						})
					}
				})

				this.revolutPayInstance = revolutPay
				if (revolutDoubleBuffer) {
					this.revolutPaySlot.swap()
					await firstValueFrom(timer(150))
					this.safeDestroy(previousInstance)
				} else {
					this.revolutPaySlot.resolve('available')
				}
			} catch (mountError) {
				this.safeDestroy(revolutPay)
				if (!this.attemptCurrent(epoch)) return false // abandoned attempt's own failure isn't worth surfacing
				console.warn('[SchmancyRevolute] Revolut Pay mount failed:', mountError)
				if (revolutDoubleBuffer) {
					// This SDK exposes no eligibility check distinct from a mount
					// failure, so every rebuild-mode failure here is treated as the
					// mere-re-price case: the old instance is still valid and stays
					// painted, per the method doc above.
					revolutKeptStale = true
					// No notice, same as the wallet slot above — the kept-stale branch
					// of updatePaymentMethodsObservable owns that decision.
					const message = 'Failed to refresh Revolut Pay for the updated amount'
					console.warn(`[SchmancyRevolute] ${message}`)
					this.dispatchEvent(new SchmancyRevoluteErrorEvent({ error: new Error(message), message }))
				} else {
					this.safeDestroy(previousInstance)
					this.revolutPaySlot.resolve('unavailable')
				}
			}
		} else {
			this.revolutPaySlot.resolve('unavailable')
		}

		return !walletKeptStale && !revolutKeptStale
	}

	/**
	 * Revolut Merchant API - Card Field Integration
	 * Official Documentation: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/card-payments/web/card-field
	 *
	 * Integration Flow:
	 * 1. Create order via Merchant API: https://developer.revolut.com/docs/api/merchant
	 * 2. Receive token from response
	 * 3. Initialize RevolutCheckout with order token
	 * 4. Create card field and mount to DOM
	 *
	 * Important Notes:
	 * - Card field uses ORDER TOKEN (token), NOT Merchant Public API Key
	 * - Each card field instance is tied to a specific order
	 * - Different from express payments which use Merchant Public API Key
	 */
	private async initializeCardField() {
		if (
			!this.enableCardField ||
			!this.cardFieldContainer.value ||
			this.cardFieldInstance ||
			this.cardInitializing ||
			(this.cardDisclosureLabel &&
				(!this.cardExpanded || this.cardFieldContainer.value.getBoundingClientRect().width <= 0))
		) {
			return
		}

		// See attemptEpoch's own doc — a reset/disconnect racing the order
		// request below never cancels this promise, so its result is checked
		// against the epoch it started with before touching any state.
		const epoch = this.attemptEpoch
		this.cardInitializing = true
		this.noticeMethod = 'card'
		try {
			// Step 1: Request order creation from parent (event-driven)
			// API Reference: https://developer.revolut.com/docs/api/merchant
			const { token } = await this.requestOrderFromParent()
			if (!this.attemptCurrent(epoch)) return // abandoned — no token was ever stored, nothing to destroy
			this.cardFieldOrderToken = token
			this.mountCardField(token)
		} catch (error: unknown) {
			if (!this.attemptCurrent(epoch)) return // an abandoned attempt's own failure isn't worth surfacing
			const message = error instanceof Error ? error.message : String(error)
			// Same buyer situation as a failed boot, different internal step: no
			// card field mounted, nothing to submit. The re-throw below reaches
			// runInitializePaymentMethods' own catch, which raises the same
			// `blocked` notice carrying this same message — the alert's own
			// latest-wins raise collapses the pair into one panel.
			// schmancy-text-literal-ok: card-field mount stand-in on PaymentNotice.detail — telemetry, never rendered
			this.notice$.next({ failure: 'blocked', detail: message || 'Failed to initialize card field' })
			throw error instanceof Error ? error : new Error(message) // Re-throw to be caught by main pipeline
		} finally {
			this.cardInitializing = false
		}
	}

	/**
	 * Creates the SDK card field against an already-issued order token. Split out
	 * of initializeCardField so connectedCallback's resolvedScheme$ leg can
	 * re-resolve fresh colors and remount the SDK's iframe on a theme flip
	 * without requesting a brand-new backend order for what is purely a restyle.
	 */
	private mountCardField(token: string): void {
		if (!this.cardFieldContainer.value) {
			return
		}

		// Clear container DOM to prevent duplicate/stale card fields on (re)mount.
		this.cardFieldContainer.value.innerHTML = ''

		// Step 2: Initialize RevolutCheckout with order token
		// Documentation: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/card-payments/web/card-field#step-3-initialize-revolut-checkout
		const revolutInstance = window.RevolutCheckout(token, this.sandboxMode ? 'sandbox' : 'prod')

		// Step 3: Create card field
		// Documentation: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/card-payments/web/card-field#step-4-render-the-payment-form
		// Note: the Revolut SDK doesn't support CSS variables — it must receive
		// computed values, so the theme tokens are resolved via getComputedStyle
		// rather than passed as var(--schmancy-sys-color-*) references. Metrics and
		// color tokens are the SAME source <schmancy-input>'s default ('md') size
		// and chrome draw from — see @schmancy/theme/control-size and
		// @schmancy/form/fields/input/input-chrome — so a change to either cascades
		// here automatically. `fontFamily` reads the host's own computed font (the
		// ambient theme font any un-styled schmancy component inherits) rather than
		// a hardcoded stack, so a font change also cascades without an edit here.
		// No `lineHeight` here: `metrics.lineHeight` (the wrapper's full box height)
		// is the vertical-centering trick for a real `<input>` that IS the box —
		// the SDK's own inner input doesn't fill our wrapper, so that line-height
		// shoves its text down and clips it; the wrapper's `flex items-center`
		// (see render()) does the centering instead.
		const metrics = controlSizeStyles('md', 'input')
		// Mirrors input.scss's own iOS fix (`@supports (-webkit-touch-callout: none) { input
		// { font-size: 16px !important } }`) — but that CSS lives in <schmancy-input>'s shadow
		// DOM and can't reach across the SDK's own iframe, so the same floor is applied here
		// via JS feature-detect instead of CSS.
		const cardFieldFontSize =
			typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('-webkit-touch-callout', 'none')
				? '16px'
				: metrics.fontSize
		this.cardFieldInstance = revolutInstance.createCardField({
			target: this.cardFieldContainer.value,
			styles: {
				default: {
					color: resolveThemeColor(SchmancyTheme.sys.color.surface.on),
					fontSize: cardFieldFontSize,
					fontFamily: getComputedStyle(this).fontFamily,
					'::placeholder': {
						// `resolveThemeColor` reads a real, standalone runtime custom property
						// off document.body — `--color-muted` (the Tailwind theme alias
						// <schmancy-input>'s `placeholder:text-muted` class uses) only ever
						// exists inside each shadow root's own adopted stylesheet, never on
						// document.body, so it resolves to '' here. surface.onVariant is the
						// SAME value that alias forwards to, and IS a real document.body property.
						color: resolveThemeColor(SchmancyTheme.sys.color.surface.onVariant),
					},
				},
				focused: {
					color: resolveThemeColor(SchmancyTheme.sys.color.surface.on),
				},
				invalid: {
					color: resolveThemeColor(SchmancyTheme.sys.color.error.default),
				},
				autofilled: {
					color: resolveThemeColor(SchmancyTheme.sys.color.surface.on),
				},
			},
			onValidation: (messages: string[]) => {
				this.validationMessages$.next(messages)
				// The buyer is working on their card again — whatever the last
				// submission concluded is no longer the current state.
				this.notice$.next(null)
				// Resolve pending validation promise if exists
				if (this.validationResolver) {
					this.validationResolver(messages)
					this.validationResolver = null
				}
			},
			// onSuccess/onError/onCancel are mutually exclusive per submission —
			// cardTerminalHandled (reset at the start of each handleCardSubmit)
			// latches the first to fire and ignores the rest.
			onSuccess: () => {
				if (this.cardTerminalHandled) return
				this.cardTerminalHandled = true
				this.isSubmittingCard$.next(false)
				this.sdkPaymentEvent$.next({ kind: 'success', method: 'card', token })
			},
			onError: (error: RevolutCheckoutError) => {
				if (this.cardTerminalHandled) return
				this.cardTerminalHandled = true
				this.isSubmittingCard$.next(false)
				this.sdkPaymentEvent$.next({
					kind: 'error',
					method: 'card',
					failure: classifyRevoluteFailure(error.type, error.code, 'card'),
					// schmancy-text-literal-ok: card vendor-error stand-in, carried to PaymentNotice.detail
					message: error.message || 'Card payment failed',
				})
			},
			// The SDK's sole channel for a cancelled/closed 3DS challenge — without
			// this, a buyer who dismisses the challenge leaves isSubmittingCard$
			// true forever (the button stuck on "Processing…", the processing
			// event never released). isSubmittingCard$.next(false) alone emits
			// SchmancyRevoluteProcessingEvent(false) via the existing leg above.
			onCancel: () => {
				if (this.cardTerminalHandled) return
				this.cardTerminalHandled = true
				this.isSubmittingCard$.next(false)
				this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_CANCELLED', 'card'))
			},
		})

		// Mark card field as ready
		this.cardFieldReady$.next(true)
		this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_METHOD_AVAILABLE', 'card'))
	}

	/**
	 * Rebuild the mounted payment methods against a re-priced order — silently.
	 * Returns an Observable that completes when the rebuild is done; composed
	 * with exhaustMap in connectedCallback so a concurrent amount/currency
	 * change while a rebuild is in flight is dropped, not queued.
	 *
	 * BOTH instance families cache the order's amount at construction — the
	 * express instances in their own paymentRequest(...)/mount(...) call, the
	 * card field in the order RevolutCheckout(token) reads at instantiation —
	 * and neither re-reads it afterwards. Revolut answers a payment POSTed at
	 * an amount its order no longer carries with a 400, so every basket edit
	 * that moves the host's `amount` prop must rebuild whichever of the two is
	 * mounted, the card field included. A re-price leaves the order token
	 * itself untouched, so the card field remounts against the SAME token it
	 * already holds; only the express leg re-requests one.
	 *
	 * The buyer sees none of this. The guard leg in connectedCallback derives
	 * `inert` (paired with the srAnnouncement text) purely from live
	 * amount$/currency$ vs. mounted$ — it has already engaged well before
	 * this method's own 300ms debounce even elapsed, so pointer, keyboard,
	 * and focus are blocked on every still-painted control for the whole
	 * window, and a tap against a stale-amount button or the mid-remount card
	 * iframe can't reach the SDK. This method itself never writes
	 * silentRebuildActive$ — its only lever is mounted$, advanced ONLY on a
	 * leg that actually completed for the value it targeted: the no-op early
	 * return (nothing was mounted to begin with, so this value is trivially
	 * "settled"), and the success path, but never the catchError branch, and
	 * never when initializeExpressPayments reports a kept-stale slot (a
	 * failed double-buffer attempt that left the old, still-valid instance
	 * painted rather than collapse it — see that method's doc). A rebuild
	 * that fails, or partially fails, therefore leaves mounted$ stale on
	 * purpose, which is what keeps the guard engaged. `finalize` fires
	 * rebuildTick$ with whatever this attempt targeted; connectedCallback's
	 * rebuild leg retries only when LIVE amount/currency has since moved on
	 * from that value — converging to the latest value when a re-price was
	 * dropped mid-flight, but never immediately retrying a value that merely
	 * failed and hasn't changed since (that recovers only from the next
	 * genuine amount/currency change).
	 *
	 * The wallet and Revolut Pay slots double-buffer (see
	 * initializeExpressPayments): the old button stays painted, unchanged,
	 * until its replacement confirms itself and cross-fades in. The card
	 * field keeps its existing skeleton cross-fade instead — createCardField's
	 * single mount target and its lack of an async "ready" signal make a
	 * clean double-buffer impossible — so it alone still visibly flips;
	 * `inert` is what keeps that window safe. loading$ (the busy overlay +
	 * 0.3 dim) is never touched here — it belongs to
	 * runInitializePaymentMethods' boot sequence alone — and no height lock
	 * is needed either: every slot's box is reserved geometry that neither
	 * this rebuild nor the double-buffered swap ever changes.
	 *
	 * The host owns the other half: `amount` must only ever move to a value
	 * the PSP order actually carries already. A rebuild fired while the host's
	 * own server-side re-price is still in flight would simply cache the old
	 * amount a second time.
	 */
	private updatePaymentMethodsObservable(amount: number, currency: string): Observable<void> {
		// Captured at this rebuild attempt's own start — see attemptEpoch's
		// doc. initializeExpressPayments guards its OWN writes internally, but
		// this function's own writes below (mountCardField, mounted$) need the
		// same guard, since a reset/disconnect racing the awaits inside
		// initializeExpressPayments never cancels this pipe's continuation.
		const epoch = this.attemptEpoch

		// Nothing mounted against the old amount to rebuild: no merchantPublicKey
		// (express payments were never offered) or every express slot already
		// resolved 'unavailable', AND no card field holding an order token. A
		// genuine no-op — nothing for the buyer to see, and nothing that could
		// be left stale, so mounted$ settles to this call's target immediately.
		// A completed payment rebuilds nothing either: remounting would raise a
		// live card field over an order already paid.
		const hasExpressToRebuild =
			!!this.merchantPublicKey &&
			(this.walletSlot.slot$.value.status !== 'unavailable' || this.revolutPaySlot.slot$.value.status !== 'unavailable')
		const cardTokenToRemount = this.enableCardField ? this.cardFieldOrderToken : null

		if (this.paymentCompleted$.value || (!hasExpressToRebuild && !cardTokenToRemount)) {
			this.mounted$.next({ amount, currency })
			return EMPTY
		}

		return of(null).pipe(
			tap(() => {
				if (cardTokenToRemount) {
					// Not ready again until the fresh iframe mounts — handleCardSubmit
					// reads this, so a tap landing mid-remount is a silent no-op rather
					// than a submit against the torn-down instance.
					this.cardFieldReady$.next(false)
					this.destroyCardFieldInstance()
				}
			}),
			// One frame so the card field's destroy() (a synchronous SDK call
			// whose iframe teardown is not) settles before mountCardField clears
			// the container and re-creates it — matters most when no express leg
			// is rebuilding, since then this is the only async gap in the chain.
			switchMap(
				() =>
					new Observable<void>(observer => {
						requestAnimationFrame(() => {
							observer.next()
							observer.complete()
						})
					}),
			),
			// The express leg double-buffers its own old-instance teardown per
			// slot (see initializeExpressPayments) — nothing to destroy upfront
			// here. fullyRebuilt is false when either express slot kept a stale
			// instance from a failed double-buffer attempt.
			switchMap(() => from(hasExpressToRebuild ? this.initializeExpressPayments('rebuild') : Promise.resolve(true))),
			tap(fullyRebuilt => {
				if (!this.attemptCurrent(epoch)) return // reset/disconnect invalidated this rebuild already
				if (cardTokenToRemount) {
					// A throw here (a genuinely broken remount) skips the mounted$
					// stamp below via the catchError branch — never a partial commit.
					this.mountCardField(cardTokenToRemount)
				}
				if (fullyRebuilt) {
					this.mounted$.next({ amount, currency })
					// There IS something to submit again, and whatever the last
					// attempt concluded was concluded against a price that has since
					// moved. Load-bearing, not hygiene: a `blocked` notice holds the
					// pay button disabled, so a rebuild that recovers must retire it
					// or the button never comes back.
					//
					// `orderStale` is the one notice a rebuild does NOT recover: the
					// card field remounts against the SAME cached order token
					// (cardTokenToRemount above), so a checkout the vendor already
					// called finished or cancelled is exactly as dead after this
					// rebuild as before it. Retiring it here would re-enable the pay
					// action against that dead token off an unrelated re-price.
					if (this.notice$.value?.failure !== 'orderStale') this.notice$.next(null)
					// A genuine settle resets the retry budget — a LATER divergence at
					// a different value earns its own one-shot auto-retry.
					this.staleRetriedFor = null
				} else {
					// Kept-stale at an unchanged amount/currency — exactly the case
					// rebuildTick$'s own filter refuses to retry (see connectedCallback's
					// merge). staleDivergence$ feeds the ONE bounded auto-retry leg there,
					// whose own filter drops this once the budget for this divergence is
					// spent.
					//
					// The FIRST kept-stale stays silent on purpose: it is auto-retried
					// within STALE_RETRY_DELAY_MS and the host's settling pay label
					// already carries the wait. The SECOND is the end of the line —
					// staleRetriedFor already naming this divergence means its one retry
					// has been taken and nothing else will fire. mounted$ never advances,
					// so the guard holds paymentMethodsContainer `inert` permanently:
					// `blocked`, not `refused`, because there is genuinely nothing left to
					// submit and a live-looking pay action behind an inert ancestor would
					// be a promise the widget cannot keep.
					const retryBudgetSpent =
						this.staleRetriedFor?.amount === amount && this.staleRetriedFor?.currency === currency
					this.staleDivergence$.next({ amount, currency })
					if (retryBudgetSpent) {
						this.notice$.next({
							failure: 'blocked',
							// schmancy-text-literal-ok: spent-retry-budget account on PaymentNotice.detail — telemetry, never rendered
							detail: `Failed to refresh the payment methods for ${amount} ${currency}, retry included`,
						})
					}
				}
			}),
			map(() => undefined),
			catchError(error => {
				if (!this.attemptCurrent(epoch)) return EMPTY // an abandoned rebuild's own failure isn't worth surfacing
				const message = error instanceof Error ? error.message : String(error)
				// A re-price rebuild that could not land. The card field was torn
				// down before the attempt and never came back, so there is nothing
				// left to submit — same buyer situation as a failed boot.
				// schmancy-text-literal-ok: re-price rebuild stand-in on PaymentNotice.detail — telemetry, never rendered
				this.notice$.next({ failure: 'blocked', detail: message || 'Failed to reinitialize payment methods' })
				this.dispatchEvent(
					new SchmancyRevoluteErrorEvent({
						error: error instanceof Error ? error : new Error(message),
						// schmancy-text-literal-ok: re-price rebuild stand-in on the error event's message — host log payload
						message: message || 'Failed to reinitialize payment methods',
						failure: 'blocked',
					}),
				)
				return EMPTY
			}),
			finalize(() => {
				// Carries what THIS attempt targeted, regardless of how it
				// concluded — the rebuild leg's own filter on rebuildTick$ decides
				// whether live has moved on from it (retry) or not (a failure
				// that hasn't changed since must not immediately retry itself).
				this.rebuildTick$.next({ amount, currency })
			}),
		)
	}

	/**
	 * Handle card field submission
	 * Documentation: https://developer.revolut.com/docs/guides/accept-payments/payment-methods/card-payments/web/card-field#step-5-submit-card-details
	 *
	 * CRITICAL: Email field requirements per Revolut SDK docs:
	 * - Email is REQUIRED if not provided when creating the order via API
	 * - Email MUST be omitted from meta object if not provided (not undefined, not empty string)
	 * - When provided, email MUST be validated for format
	 */
	private handleCardSubmit = async () => {
		if (this.validatingCard || this.isSubmittingCard$.value || this.settling || this.paymentCompleted$.value) return
		this.noticeMethod = 'card'
		this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_METHOD_CLICKED', 'card'))

		// The host gates on its own contact validation and renders its own remedy —
		// this widget stays silent rather than duplicating that reason text.
		if (this.submitDisabled) {
			return
		}

		if (!this.cardFieldInstance) {
			// Developer-facing wording for the same buyer fact as a failed card-field
			// init: there is no form behind this button. The detail rides the error
			// event so a host that never sees the console still gets it.
			const message = 'Card field not initialized'
			console.error(`[SchmancyRevolute] Cannot submit - ${message}`)
			this.notice$.next({ failure: 'blocked', detail: message })
			this.dispatchEvent(new SchmancyRevoluteErrorEvent({ error: new Error(message), message, failure: 'blocked' }))
			return
		}

		if (!this.cardFieldReady$.value) {
			console.warn('[SchmancyRevolute] Card field not ready for submission')
			return
		}

		// Host-provided email is assumed already validated by the host; the
		// internal input (rendered only when `email` is unset) is validated here.
		// Host ownership is property PRESENCE — an empty host email means "not
		// yet entered", and the host's submitDisabled gate carries that case.
		let emailValue: string
		if (this.email !== undefined) {
			emailValue = this.email
		} else {
			const trimmed = this.emailInput?.trim() ?? ''
			if (!trimmed) {
				this.emailError = componentText('payment.email.error.required')
				this.dispatchEvent(
					new SchmancyRevoluteTrackingEvent('CARD_VALIDATION_FAILED', 'card', { reason: 'email_missing' }),
				)
				return
			}
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(trimmed)) {
				this.emailError = componentText('payment.email.error.invalid')
				this.dispatchEvent(
					new SchmancyRevoluteTrackingEvent('CARD_VALIDATION_FAILED', 'card', { reason: 'email_invalid' }),
				)
				return
			}
			emailValue = trimmed
		}

		// Validate card asynchronously — wait for the Revolut SDK's onValidation
		// callback, racing a 500ms fallback in case it never fires.
		this.validatingCard = true
		if (this.cardDisclosure.value) this.cardDisclosure.value.locked = true
		this.updateSubmitButtonState()
		let validationErrors: string[]
		try {
			validationErrors = await firstValueFrom(
				race(
					new Observable<string[]>(observer => {
						this.validationResolver = messages => {
							observer.next(messages)
							observer.complete()
						}
						this.cardFieldInstance.validate()
					}),
					timer(500).pipe(map(() => this.validationMessages$.value)),
				).pipe(
					tap(() => {
						this.validationResolver = null
					}),
				),
			)
		} catch (error) {
			this.validatingCard = false
			this.updateSubmitButtonState()
			throw error
		} finally {
			this.validatingCard = false
		}
		if (validationErrors.length > 0) {
			this.updateSubmitButtonState()
			this.dispatchEvent(
				new SchmancyRevoluteTrackingEvent('CARD_VALIDATION_FAILED', 'card', {
					reason: 'card_invalid',
					errors: validationErrors,
				}),
			)
			// No notice. This is a FIELD error, and the card slot already carries
			// it in full (validationMessages$ → the wrapper's chrome AND the words
			// in its own hint region) — a panel saying the same thing is the buyer
			// reading one fact twice.
			// Inline field errors stay inline; the alert is about submission alone.
			// Focus moves to the field that has to change instead.
			this.cardFieldWrapper.value?.focus()
			return
		}

		of(null)
			.pipe(
				tap(() => {
					this.isSubmittingCard$.next(true)
					this.notice$.next(null)
					this.cardTerminalHandled = false
					this.dispatchEvent(new SchmancyRevoluteTrackingEvent('CARD_SUBMIT_STARTED', 'card'))
				}),
				tap(() => {
					// A name the buyer never gave is a claim the issuer can reject as a
					// mismatch, and only a claim can mismatch — so an absent one is sent
					// as absent, even though the vendor maps a missing cardholder name
					// to its own decline.
					const payerName = this.payerName?.trim()
					this.cardFieldInstance.submit({
						...(payerName && { name: payerName }),
						...(this.billingAddress && { billingAddress: this.billingAddress }),
						email: emailValue,
					})
				}),
				catchError(error => {
					console.error('[SchmancyRevolute] Card submission failed:', error)
					const message = error instanceof Error ? error.message : String(error)
					// The submit call threw before the SDK ever answered, so there is no
					// `type`/`code` to sort on — `refused` is the honest fallback, and
					// the buyer situation is a decline's: money was asked for and did
					// not move, and the form is still there.
					// schmancy-text-literal-ok: card-submit throw stand-in on PaymentNotice.detail — telemetry, never rendered
					this.notice$.next({ failure: 'refused', detail: message || 'Failed to submit card payment' })
					this.isSubmittingCard$.next(false)
					this.cardTerminalHandled = true
					this.dispatchEvent(new SchmancyRevoluteTrackingEvent('PAYMENT_ERROR', 'card', { error: message }))
					this.dispatchEvent(
						new SchmancyRevoluteErrorEvent({
							error: error instanceof Error ? error : new Error(message),
							// schmancy-text-literal-ok: card-submit throw stand-in on the error event's message — host log payload
							message: message || 'Failed to submit card payment',
							failure: 'refused',
						}),
					)
					return EMPTY
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	private handleNoticeRemedy = () => {
		// Whichever of the two got the widget here is the one that
		// answers; the other is a no-op. A never-booted widget
		// recovers through the boot request (the rebuild leg
		// early-returns while paymentMethodsInitialized$ is false);
		// a rebuild that THREW has no automatic recovery at all —
		// staleDivergence$ only covers the kept-stale case and
		// rebuildTick$'s own filter refuses a value live hasn't
		// moved on from — so this tick, carrying the stale mounted
		// pair, is the only thing that re-enters it.
		this.notice$.next(null)
		this.initializePaymentMethodsIfNeeded()
		this.rebuildTick$.next({
			amount: this.mounted$.value.amount ?? 0,
			currency: this.mounted$.value.currency ?? '',
		})
	}

	override render() {
		// The card slot's box — same (size, role) metrics <schmancy-input>'s default
		// 'md' size derives its own box from (@schmancy/theme/control-size).
		const cardFieldMetrics = controlSizeStyles('md', 'input')

		const cardForm = html`
			<!-- Card Field - Wrapped in surface for visual grouping -->
			<schmancy-surface
				type="containerLowest"
				class="rounded-lg transition-opacity duration-200"
				style="opacity: ${this.enableCardField ? '1' : '0'}; visibility: ${
					this.enableCardField ? 'visible' : 'hidden'
				}; contain: layout style;"
			>
				<div class="flex flex-col gap-3">
					<!-- Email input for card payments - omitted when the host already owns contact fields -->
					${when(
						this.email === undefined,
						() => html`
							<schmancy-input
								class="w-full"
								type="email"
								label=${componentText('payment.email.label')}
								placeholder=${componentText('payment.email.placeholder')}
								.value=${this.emailInput}
								.error=${!!this.emailError}
								.validationMessage=${this.emailError}
								@change=${(e: SchmancyInputChangeEvent) => {
									this.emailInput = e.detail.value
									// Clear email error when user types
									if (this.emailError) {
										this.emailError = ''
									}
									// Emit email change for parent to persist (basic validation)
									const emailValue = e.detail.value.trim()
									if (emailValue && emailValue.includes('@') && emailValue.includes('.')) {
										this.dispatchEvent(new SchmancyRevoluteEmailChangeEvent(emailValue))
									}
								}}
							></schmancy-input>
						`,
					)}

					<slot name="card-billing"></slot>

					<div class="flex flex-col gap-3">
						<!-- Card slot - the SAME chrome (radius/border/ring/focus-within) and box
								     metrics <schmancy-input>'s default size renders, since a Revolut SDK
								     iframe stands in for the <input> element here. Fixed reserved height;
								     the SDK iframe (re)mounts behind a skeleton that cross-fades out on
								     opacity alone, so a reinit never shows a blank box between destroy and
								     the new iframe's load. createCardField's single mount target and its
								     lack of an async "ready" signal make double-buffering it unclean, so a
								     silent re-price rebuild still flips this skeleton (unlike the
								     wallet/Revolut Pay slots, which double-buffer silently) — inert on
								     paymentMethodsContainer is what keeps the outgoing iframe from
								     accepting input meanwhile, not a visual absence of change. -->
						<!-- tabindex so a submit blocked by card-field validation can move
								     focus to the field that has to change — the wrapper is the
								     only reachable handle, since the input itself lives inside
								     the SDK's own cross-origin iframe. -->
						<!-- The field and its own message are one unit — grouped so the
								     stack's own gap-3 never opens between them, exactly as
								     <schmancy-input> keeps its own hint block against its
								     <input>. -->
						<div>
							<div
								${ref(this.cardFieldWrapper)}
								tabindex="-1"
								aria-invalid="false"
								aria-describedby="card-field-hint"
								class=${this.classMap({ 'relative w-full outline-none': true, ...fieldChromeClasses(false, true) })}
								style="height: ${cardFieldMetrics.height}; contain: layout style;"
							>
								<schmancy-skeleton
									${ref(this.cardFieldSkeleton)}
									shape="rect"
									class="absolute inset-0 rounded-lg transition-opacity duration-150 ease-out motion-reduce:transition-none"
									style="opacity: 1;"
								></schmancy-skeleton>
								<div
									${ref(this.cardFieldContainer)}
									class="absolute inset-0 flex items-center transition-opacity duration-150 ease-out motion-reduce:transition-none"
									style="padding: ${cardFieldMetrics.padding}; opacity: 0;"
								></div>
							</div>
							<!-- The card field's own words — the half of <schmancy-input>'s
									     treatment a shared chrome class map cannot carry, and without
									     which the red border says only that SOMETHING is wrong.
									     Written by validationMessages$ (connectedCallback), never by
									     a render: the wrapper it describes is ref-driven too, and
									     the two have to move in the same tick. Present from first
									     paint rather than conditionally created, so its role="alert"
									     is a live region the SDK's validation announces INTO — a
									     region created together with its own first text is one
									     several screen readers never announce at all. -->
							<div
								${ref(this.cardFieldHint)}
								id="card-field-hint"
								role="alert"
								class="mt-1 text-sm text-error-default empty:hidden"
							></div>
						</div>

						<!-- The failure notice's reserved slot: directly ABOVE the action,
								     never below it. The action is the last element of the payment
								     stack, so anything under it competes with the fold, and
								     reason-then-action is the reading order that makes sense of
								     both. Takes no box at all until something is raised. -->
						<schmancy-alert ${ref(this.alert)} @remedy=${this.handleNoticeRemedy}></schmancy-alert>

						<!-- updateSubmitButtonState owns the label; pending keeps the clicked action's text. -->
						<schmancy-button
							${ref(this.submitButton)}
							size="lg"
							width="full"
							variant="outlined"
							@click=${() => this.handleCardSubmit()}
						></schmancy-button>
					</div>
				</div>
			</schmancy-surface>
		`

		return html`
			<div ${ref(this.paymentContainer)} class="relative flex flex-col" style="contain: layout style;">
				<!-- Widget-owned narration of a silent rebuild: inert (below) pulls
				     the wallet buttons out of the accessibility tree and tab order with
				     no other explanation, and this rebuild outlives the host's own
				     settling-window announcement — so this widget carries its own. -->
				<div ${ref(this.srAnnouncement)} class="sr-only" role="status" aria-live="polite"></div>
				<!-- Overlay - loading$ only ever goes true during the initial boot
				     sequence (runInitializePaymentMethods); a silent re-price rebuild
				     never touches it. Opacity-only transition, no layout shift. -->
				<schmancy-busy
					class="absolute inset-0 z-50 transition-opacity duration-200"
					${ref(this.busyOverlayContainer)}
					style="opacity: 0; pointer-events: none;"
				></schmancy-busy>
				<!-- Payment containers - opacity driven by loading$ (boot only, per
				     above). A silent re-price rebuild instead toggles inert on this
				     element for its duration — blocks pointer/keyboard/focus on every
				     still-painted control with zero visual change, so a stale-amount
				     button can't be tapped while its replacement mounts. -->
				<div
					${ref(this.paymentMethodsContainer)}
					class="flex flex-col gap-3 transition-opacity duration-200 ease-in-out"
					style="opacity: 1;"
				>
					<!-- Apple Pay / Google Pay slot - reserved at settled height from first
					     paint; collapses only on a definitive canMakePayment failure. Both
					     mount containers start inert (opacity 0, pointer-events none) so
					     the pre-subscription paint is inert by default — walletSlot.render$()
					     (connectedCallback) is the ONLY writer of every channel below from
					     here on, so front/back can never disagree about which is clickable. -->
					<div
						${ref(this.walletSlot.section)}
						class="w-full rounded-xl focus-within:outline-solid focus-within:outline-2 focus-within:outline-primary-default focus-within:outline-offset-2 data-[focused]:outline-solid data-[focused]:outline-2 data-[focused]:outline-primary-default data-[focused]:outline-offset-2 transition-all duration-300 ease-out overflow-hidden motion-reduce:transition-none"
						style="max-height: ${WALLET_SLOT_HEIGHT_PX}px; opacity: 1;"
					>
						<div class="relative w-full" style="height: ${WALLET_SLOT_HEIGHT_PX}px;">
							<schmancy-skeleton
								${ref(this.walletSlot.skeleton)}
								shape="rect"
								class="absolute inset-0 w-full transition-opacity duration-150 ease-out motion-reduce:transition-none"
								style="opacity: 1;"
							></schmancy-skeleton>
							<!-- SDK mounts buttons directly into whichever of A/B is currently
							     front (visible) - no label needed, buttons are self-explanatory.
							     The other container is a silent rebuild's double-buffer mount
							     target for a fresh instance (walletSlot.slot$.front tracks which). -->
							<div
								${ref(this.walletSlot.containerA)}
								class="absolute inset-0 w-full transition-opacity duration-150 ease-out motion-reduce:transition-none"
								style="opacity: 0; pointer-events: none;"
							></div>
							<div
								${ref(this.walletSlot.containerB)}
								class="absolute inset-0 w-full transition-opacity duration-150 ease-out motion-reduce:transition-none"
								style="opacity: 0; pointer-events: none;"
							></div>
						</div>
					</div>

					${when(this.cardDisclosureLabel, () => html`<schmancy-alert ${ref(this.walletAlert)} @remedy=${this.handleNoticeRemedy}></schmancy-alert>`)}

					<!-- Revolut Pay slot - same reserve-until-resolved shape as the wallet slot above. -->
					<div
						${ref(this.revolutPaySlot.section)}
						class="w-full rounded-xl focus-within:outline-solid focus-within:outline-2 focus-within:outline-primary-default focus-within:outline-offset-2 data-[focused]:outline-solid data-[focused]:outline-2 data-[focused]:outline-primary-default data-[focused]:outline-offset-2 transition-all duration-300 ease-out overflow-hidden motion-reduce:transition-none"
						style="max-height: ${WALLET_SLOT_HEIGHT_PX}px; opacity: 1;"
					>
						<div class="relative w-full" style="height: ${WALLET_SLOT_HEIGHT_PX}px;">
							<schmancy-skeleton
								${ref(this.revolutPaySlot.skeleton)}
								shape="rect"
								class="absolute inset-0 w-full transition-opacity duration-150 ease-out motion-reduce:transition-none"
								style="opacity: 1;"
							></schmancy-skeleton>
							<!-- Double-buffer mount targets, same pattern as the wallet slot above. -->
							<div
								${ref(this.revolutPaySlot.containerA)}
								class="absolute inset-0 w-full transition-opacity duration-150 ease-out motion-reduce:transition-none"
								style="opacity: 0; pointer-events: none;"
							></div>
							<div
								${ref(this.revolutPaySlot.containerB)}
								class="absolute inset-0 w-full transition-opacity duration-150 ease-out motion-reduce:transition-none"
								style="opacity: 0; pointer-events: none;"
							></div>
						</div>
					</div>

					${when(this.cardDisclosureLabel, () => html`<schmancy-alert ${ref(this.revolutPayAlert)} @remedy=${this.handleNoticeRemedy}></schmancy-alert>`)}
					${when(
						this.cardDisclosureLabel,
						() => html`
							<schmancy-details
								${ref(this.cardDisclosure)}
								.open=${this.cardExpanded}
								.locked=${this.validatingCard || this.isSubmittingCard$.value || this.settling}
								aria-label=${ifDefined(this.cardDisclosureLabel)}
								content-padding="p-4"
								variant="outlined"
								size="md"
								@toggle=${(event: CustomEvent<{ open: boolean }>) => {
									this.cardExpanded = event.detail.open
									if (event.detail.open) {
										if (this.noticeMethod !== 'card') this.notice$.next(null)
										this.cardOpenRequested$.next()
									}
								}}
							>
								<div slot="summary" class="flex w-full items-center justify-between gap-4">
									<schmancy-typography type="title" token="sm">${this.cardDisclosureLabel}</schmancy-typography>
									<slot name="card-summary"></slot>
								</div>
								${cardForm}
							</schmancy-details>
						`,
						() => cardForm,
					)}
				</div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-revolute': SchmancyRevolute
	}
	interface HTMLElementEventMap {
		'schmancy-revolute-request-order': SchmancyRevoluteRequestOrderEvent
		'schmancy-revolute-success': SchmancyRevoluteSuccessEvent
		'schmancy-revolute-error': SchmancyRevoluteErrorEvent
		'schmancy-revolute-tracking': SchmancyRevoluteTrackingEvent
		'schmancy-revolute-email-change': SchmancyRevoluteEmailChangeEvent
	}
}
